import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Paper,
  Tooltip,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Chip,
  ClickAwayListener,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popper,
  Avatar,
  ListItemAvatar,
  InputAdornment,
  CircularProgress,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import { enhancePrompt, deletePinnedPromptForAgent } from "../api/agentApi";
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  PlayArrow as RunIcon,
  AttachFile as AttachFileIcon,
  Check as CheckIcon,
  Description,
  EditOutlined as EditIcon,
  History as HistoryIcon,
  Image as ImageIcon,
  InsertDriveFile as InsertDriveFileIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  PushPin as PushPinIcon,
  Share as ShareIcon,
  Link as LinkIcon,
  LinkedIn as LinkedInIcon,
  Reddit as RedditIcon,
  OpenInNew as OpenInNewIcon,
  DriveFolderUpload as DriveFolderUploadIcon,
  CloudUpload as CloudUploadIcon,
  FolderOpen as FolderOpenIcon,
} from "@mui/icons-material";
import {
  getAgentRunResultFromUrl,
  createPinnedPromptForAgent,
  listPinnedPromptsForAgent,
  listAllAgentRuns,
  getAgentRunArtifacts,
  downloadArtifactFile,
  API_BASE,
  getAuthToken,
  getAgentRunPrompt,
  getAgentRunResult,
  streamAgentRunResult,
  startOAuthConnect,
  uploadAgentChatSessionFiles,
} from "../api/agentApi";
import { createAgentChatSession, getAgent, listAgentChatMessages, renameAgentRun, sendAgentChatMessage, submitAgentOutcomeFeedback } from "../api/agentApi";
import { useParams } from "react-router-dom";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PromptEnhancerDialog from "./AgentDetail/PromptEnhancerDialog";
import { AgentPinnedPrompt } from "../types";
import ArtifactsAndPinnedPromptsSideBar from "./AgentDetail/ArtifactsAndPinnedPromptsSideBar";
import HistorySidebar from "./AgentDetail/HistorySidebar";
import PinPromptDropdown from "./AgentDetail/PinPromptDropdown";
import AgentResponseDisplay from "./AgentDetail/AgentResponseDisplay";
import ThinkingIndicator from "./AgentDetail/ThinkingIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import StopIcon from '@mui/icons-material/Stop';
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { parsePrompt } from "./UIUtils";
import ReactMarkdown from "react-markdown";
import { useTheme } from "@mui/material/styles";
import { PAGE_SHELL, PAGE_TYPOGRAPHY, UI_RADIUS } from "../constants";


type AuthRequiredSignal = {
  provider?: string;
  provider_label?: string;
  required_env?: string;
  tool_name?: string | null;
  message?: string;
  request?: Record<string, any>;
  scopes?: string[];
};

type ChatTranscriptItem = {
  id: string;
  sessionId?: string | null;
  backendMessageId?: string;
  role: "user" | "assistant";
  content: string;
  status?: "completed" | "streaming" | "failed";
  runNumber?: number | null;
  artifacts?: any[];
  artifactsPending?: boolean;
  prompt?: string;
  feedback?: "up" | "down" | null;
  feedbackPending?: boolean;
  authRequired?: AuthRequiredSignal[];
};

type SharePayload = {
  kind: "prompt" | "response" | "run";
  title: string;
  text: string;
  url: string;
};

type AssetUploadStatus = "idle" | "uploading" | "indexing" | "attaching" | "success" | "error";

type AssetUploadNotice = {
  indexName: string;
  fileCount: number;
};

type UploadableFile = File & {
  relativePath?: string;
  webkitRelativePath?: string;
};

type FileReference = {
  id: string;
  name: string;
  version: string;
  runNumber: number;
  thumbnail?: string;
};

const COPPER = "#B87333";
const COPPER_DARK = "#8A5523";
const COPPER_BORDER = "rgba(184, 115, 51, 0.58)";
const SURFACE = "#FFFDFC";
const TEXT_PRIMARY = "#111827";
const TEXT_MUTED = "#6B7280";
const ACTIVE_RUN_STATUSES = new Set(["queued", "pending", "processing", "running"]);

const normalizeRunStatus = (status?: string | null) =>
  String(status || "").trim().toLowerCase();

const isActiveRunStatus = (status?: string | null) =>
  ACTIVE_RUN_STATUSES.has(normalizeRunStatus(status));

const copperChipSx = {
  bgcolor: "transparent",
  border: `1px solid ${COPPER_BORDER}`,
  color: COPPER_DARK,
  "& .MuiChip-label": {
    fontSize: "0.75rem",
    fontWeight: 600,
  },
  "& .MuiChip-icon": {
    color: COPPER_DARK,
  },
  "&:hover": {
    bgcolor: "rgba(184, 115, 51, 0.07)",
    borderColor: COPPER,
  },
};

const messageActionButtonSx = (isDark = false, disabled = false) => {
  const disabledColor = isDark ? "rgba(255,247,239,0.34)" : "#CBD5E1";
  const idleColor = isDark ? "#D9C9BA" : "#64748B";
  const activeColor = isDark ? "#F2C69B" : COPPER_DARK;
  const idleBg = isDark ? "rgba(255,247,239,0.06)" : "rgba(255, 255, 255, 0.84)";
  const disabledBg = isDark ? "rgba(255,247,239,0.04)" : "rgba(248, 250, 252, 0.64)";
  const idleBorder = isDark ? "rgba(224,177,132,0.24)" : "rgba(184, 115, 51, 0.28)";
  const disabledBorder = isDark ? "rgba(255,247,239,0.10)" : "rgba(148, 163, 184, 0.22)";

  return {
  width: 30,
  height: 30,
  minWidth: 30,
  borderRadius: UI_RADIUS.iconButton,
  color: disabled ? disabledColor : idleColor,
  border: `1px solid ${disabled ? disabledBorder : idleBorder}`,
  bgcolor: disabled ? disabledBg : idleBg,
  boxShadow: disabled ? "none" : isDark ? "0 10px 24px rgba(0,0,0,0.26)" : "0 4px 14px rgba(17, 24, 39, 0.06)",
  transition: "transform 0.16s ease, border-color 0.16s ease, color 0.16s ease, box-shadow 0.16s ease",
  "& .MuiSvgIcon-root": { fontSize: 16.5 },
  "&:hover": {
    color: disabled ? disabledColor : activeColor,
    borderColor: disabled ? disabledBorder : isDark ? "rgba(224,177,132,0.42)" : COPPER,
    bgcolor: disabled ? disabledBg : isDark ? "rgba(224,177,132,0.12)" : "#FFFFFF",
    transform: disabled ? "none" : "translateY(-1px)",
    boxShadow: disabled ? "none" : isDark ? "0 14px 30px rgba(0,0,0,0.36)" : "0 8px 22px rgba(17, 24, 39, 0.1)",
  },
  };
};

const userBubbleSx = {
  px: 2.25,
  py: 1.35,
  borderRadius: "18px",
  bgcolor: SURFACE,
  border: `1px solid ${COPPER_BORDER}`,
  color: TEXT_PRIMARY,
  boxShadow: "0 10px 30px rgba(17, 24, 39, 0.04)",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

const assistantBubbleSx = {
  px: { xs: 2, md: 2.5 },
  py: 2,
  borderRadius: "18px",
  bgcolor: SURFACE,
  border: "1px solid rgba(184, 115, 51, 0.35)",
  color: TEXT_PRIMARY,
  boxShadow: "0 12px 34px rgba(17, 24, 39, 0.045)",
  overflowWrap: "anywhere",
  wordBreak: "break-word",
  "& p": { m: 0, mb: 1 },
  "& p:last-child": { mb: 0 },
  "& ul, & ol": { pl: 2.75, m: 0, mb: 1 },
  "& li": { mb: 0.35 },
  "& pre": {
    overflowX: "auto",
    bgcolor: "rgba(15,23,42,0.05)",
    borderRadius: 1.5,
    p: 1.25,
  },
  "& code": {
    bgcolor: "rgba(184, 115, 51, 0.09)",
    color: COPPER_DARK,
    borderRadius: "5px",
    px: 0.55,
    py: 0.15,
  },
  "& img": { maxWidth: "100%" },
};

const composerSx = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    borderRadius: "26px",
    paddingBottom: "52px",
    bgcolor: "#FFFFFF",
    boxShadow: "0 18px 48px rgba(17, 24, 39, 0.08)",
    "& fieldset": {
      borderColor: COPPER_BORDER,
      borderWidth: "1.5px",
    },
    "&:hover fieldset": {
      borderColor: COPPER,
    },
    "&.Mui-focused fieldset": {
      borderColor: COPPER,
      borderWidth: "2px",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "18px 16px",
    color: TEXT_PRIMARY,
    overflowWrap: "anywhere",
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
  },
  "& textarea": {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
  },
};

const composerAdornmentSx = {
  position: "absolute",
  bottom: 12,
  right: 14,
  gap: 1,
  alignItems: "center",
};

const copperRoundButtonSx = {
  bgcolor: COPPER,
  borderRadius: UI_RADIUS.iconButton,
  width: 40,
  height: 40,
  color: "#FFFFFF",
  boxShadow: "0 10px 24px rgba(184, 115, 51, 0.24)",
  "&:hover": {
    bgcolor: COPPER_DARK,
    boxShadow: "0 12px 28px rgba(138, 85, 35, 0.28)",
  },
  "&.Mui-disabled": {
    bgcolor: "rgba(17, 24, 39, 0.12)",
    color: "rgba(17, 24, 39, 0.28)",
    boxShadow: "none",
  },
};

const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp", ".svg"];

const isImageArtifact = (artifact: any) => {
  const type = String(artifact?.type || "").toLowerCase();
  const filename = String(artifact?.filename || artifact?.name || "").toLowerCase();
  return type === "image" || IMAGE_EXTENSIONS.some((ext) => filename.endsWith(ext));
};

const normalizeRunArtifact = (
  artifact: any,
  context: { agentId: string; agentVersion: number; runNumber: number },
  index: number,
) => {
  const filename = artifact.filename || artifact.name || "Unknown";
  return {
    ...artifact,
    id: `${context.agentVersion}-${context.runNumber}-${filename}-${index}`,
    name: artifact.name || filename,
    filename,
    type: artifact.type || "",
    url:
      artifact.url ||
      `/agent/${context.agentId}/version/${context.agentVersion}/runs/${context.runNumber}/artifact/${encodeURIComponent(filename)}`,
    agent_id: context.agentId,
    agent_version: context.agentVersion,
    run_number: context.runNumber,
  };
};

const artifactIdentity = (artifact: any) =>
  `${artifact?.agent_id || ""}:${artifact?.agent_version || ""}:${artifact?.run_number || ""}:${artifact?.filename || artifact?.name || artifact?.id || ""}`;

const mergeArtifacts = (current: any[], incoming: any[]) => {
  const byIdentity = new Map(current.map((artifact) => [artifactIdentity(artifact), artifact]));
  incoming.forEach((artifact) => {
    const key = artifactIdentity(artifact);
    byIdentity.set(key, { ...(byIdentity.get(key) || {}), ...artifact });
  });
  return Array.from(byIdentity.values());
};

const filenameFromPath = (value: string) => {
  const raw = String(value || "").trim();
  const withoutQuery = raw.split(/[?#]/)[0];
  const filename = withoutQuery.split("/").filter(Boolean).pop() || raw;
  try {
    return decodeURIComponent(filename);
  } catch {
    return filename;
  }
};

const filenameFromArtifactUrl = (url: string) => {
  const artifactPart = String(url || "").split("/artifact/")[1];
  return filenameFromPath(artifactPart || url);
};

const sanitizeArtifactLabel = (label: string, url?: string) => {
  const clean = String(label || "").trim().replace(/^`|`$/g, "");
  if (!clean || clean.includes("/") || clean.includes("\\") || clean.length > 80) {
    return url ? filenameFromArtifactUrl(url) : filenameFromPath(clean);
  }
  return clean;
};

const sanitizeArtifactReferences = (text: string) => {
  let next = String(text || "");
  if (!next) return next;

  if (next.includes("/agent/")) {
    next = next.replace(/\\(\[|]|\(|\))/g, "$1");
  }

  next = next.replace(
    /`(\[[^\]\n]+\]\(\/agent\/[^)\n]+\/artifact\/[^)\n]+\))`/g,
    "$1",
  );

  next = next.replace(
    /`(\/agent\/[^`\s)]+\/version\/[^`\s)]+\/runs\/[^`\s)]+\/artifact\/[^`\s)]+)`/g,
    (_match, url) => filenameFromArtifactUrl(url),
  );

  next = next.replace(
    /\[([^\]\n]+)\]\((\/agent\/[^)\s`]+\/version\/[^)\s`]+\/runs\/[^)\s`]+\/artifact\/[^)\s`]+)\)/g,
    (_match, label, url) => sanitizeArtifactLabel(label, url),
  );

  next = next.replace(
    /(^|[\s:])((?:\/Users|\/home|\/opt|\/var|\/tmp|\/private)\/[^\s)`]+?\.(?:png|jpe?g|gif|bmp|webp|svg))/gi,
    (_match, prefix, localPath) => `${prefix}${filenameFromPath(localPath)}`,
  );

  next = next.replace(
    /(^|[\s:])(\/agent\/[^\s)`]+\/version\/[^\s)`]+\/runs\/[^\s)`]+\/artifact\/[^\s)`]+)/g,
    (_match, prefix, url) => `${prefix}${filenameFromArtifactUrl(url)}`,
  );

  return next;
};

const normalizeTranscriptStatus = (status?: string | null): ChatTranscriptItem["status"] => {
  const normalized = normalizeRunStatus(status);
  if (normalized === "failed" || normalized === "error") return "failed";
  if (normalized === "pending" || normalized === "streaming" || normalized === "running") return "streaming";
  return "completed";
};

const buildTranscriptFromChatMessages = (
  messages: any[],
  sessionId: string,
  run: any,
  runArtifacts: any[],
  fallbackPrompt: string,
  fallbackResult: string,
): ChatTranscriptItem[] => {
  const runNumber = Number(run?.run_number);
  let lastUserPrompt = "";
  const transcript = (messages || [])
    .filter((message) => message?.role === "user" || message?.role === "assistant")
    .map((message, index) => {
      const role: "user" | "assistant" = message.role === "assistant" ? "assistant" : "user";
      const content = sanitizeArtifactReferences(String(message.content || ""));
      if (role === "user") {
        lastUserPrompt = content;
      }
      const metadata = message.metadata || {};
      const outcomeFeedback = metadata.outcome_feedback || {};
      const feedback =
        outcomeFeedback.feedback === "up" || outcomeFeedback.feedback === "down"
          ? outcomeFeedback.feedback
          : null;
      const messageRunNumber =
        message.run_number === null || message.run_number === undefined
          ? null
          : Number(message.run_number);
      const item: ChatTranscriptItem = {
        id: String(message.id || `${role}-${index}`),
        sessionId,
        backendMessageId: role === "assistant" && message.id ? String(message.id) : undefined,
        role,
        content,
        status: normalizeTranscriptStatus(message.status),
        runNumber: messageRunNumber,
        prompt: role === "assistant" ? lastUserPrompt || fallbackPrompt : undefined,
        feedback,
        authRequired: role === "assistant" && Array.isArray(metadata.auth_required) ? metadata.auth_required : undefined,
      };
      if (role === "assistant" && messageRunNumber === runNumber) {
        item.artifacts = runArtifacts;
        item.artifactsPending = item.status === "streaming";
      }
      return item;
    });

  const hasSelectedAssistant = transcript.some(
    (message) => message.role === "assistant" && Number(message.runNumber) === runNumber,
  );
  if (!hasSelectedAssistant && (fallbackResult || runArtifacts.length > 0)) {
    transcript.push({
      id: `run-${run?.agent_version ?? run?.version}-${runNumber}-assistant`,
      sessionId,
      role: "assistant",
      content: sanitizeArtifactReferences(fallbackResult || ""),
      status: "completed",
      runNumber,
      prompt: fallbackPrompt,
      artifacts: runArtifacts,
    });
  }

  return transcript;
};

const AgentRunPage: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { agentId } = useParams<{ agentId: string }>();
  // History Sidebar
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [agentRuns, setAgentRuns] = useState<any[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(20);
  const [totalHistoryRuns, setTotalHistoryRuns] = useState(0);
  const [isLoadingMoreHistory, setIsLoadingMoreHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  const [runStatus, setRunStatus] = useState<any>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [finalResult, setFinalResult] = useState("");
  const [chatTranscript, setChatTranscript] = useState<ChatTranscriptItem[]>([]);
  const [thinkingDots, setThinkingDots] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [agent, setAgent] = useState<any>(null);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [enhancedSuggestion, setEnhancedSuggestion] = useState("");
  const [pinnedPrompts, setPinnedPrompts] = useState<AgentPinnedPrompt[]>([]);
  const [deleting, setDeleting] = useState("");
  const [error, setError] = useState("");
  const [hasRunTriggered, setHasRunTriggered] = useState(false);
  const [isReturningToProgress, setIsReturningToProgress] = useState(false);
  const [isHistoricalPromptExpanded, setIsHistoricalPromptExpanded] = useState(false);
  const pollTimeoutRef = React.useRef<any>(null);
  const dotsIntervalRef = React.useRef<any>(null);
  const chatStreamCleanupRef = React.useRef<any>(null);
  const chatSessionIdRef = React.useRef<string | null>(null);
  const oauthRetryRef = React.useRef<{ prompt: string; agentVersion?: number | null; runNumber?: number | null } | null>(null);
  const handleRunRef = React.useRef<((promptOverride?: string, options?: { useSelectedFiles?: boolean }) => Promise<void>) | null>(null);
  const hasAutoResumedRef = React.useRef(false); // Prevents double-resume on mount
  const isMountedRef = React.useRef(true); // Guards setState calls after unmount
  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (chatStreamCleanupRef.current) {
        chatStreamCleanupRef.current();
        chatStreamCleanupRef.current = null;
      }
    };
  }, []);
  const [artifacts, setArtifacts] = useState<any[]>([]);
  const [oauthConnectingKey, setOauthConnectingKey] = useState<string | null>(null);
  const [liveChatArtifacts, setLiveChatArtifacts] = useState<any[]>([]); // Store live chat artifacts
  const [runsRefreshToken, setRunsRefreshToken] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [selectedRunHistory, setSelectedRunHistory] = useState<any>(null);
  const [historicalPrompt, setHistoricalPrompt] = useState("");
  const [historicalResult, setHistoricalResult] = useState("");
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);
  const [historicalChatSessionId, setHistoricalChatSessionId] = useState<string | null>(null);
  const [historicalThreadNotice, setHistoricalThreadNotice] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const assetFileInputRef = useRef<HTMLInputElement>(null);
  const assetFolderInputRef = useRef<HTMLInputElement>(null);
  const [assetUploadStatus, setAssetUploadStatus] = useState<AssetUploadStatus>("idle");
  const [assetUploadError, setAssetUploadError] = useState("");
  const [assetDropActive, setAssetDropActive] = useState(false);
  const [assetUploadNotice, setAssetUploadNotice] = useState<AssetUploadNotice | null>(null);
  const [feedbackState, setFeedbackState] = useState<"up" | "down" | null>(
    null,
  );
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [sidePanelTab, setSidePanelTab] = useState<0 | 1>(0);
  const [favoriteRunPendingKeys, setFavoriteRunPendingKeys] = useState<Set<string>>(new Set());
  const [shareDialog, setShareDialog] = useState<SharePayload | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const chatBottomRef = useRef<HTMLDivElement | null>(null);
  const liveChatSnapshotRef = useRef<{
    chatSessionId: string | null;
    chatTranscript: ChatTranscriptItem[];
    artifacts: any[];
    liveChatArtifacts: any[];
    inputValue: string;
    submittedPrompt: string;
    selectedFiles: FileReference[];
    hasRunTriggered: boolean;
    finalResult: string;
    runStatus: any;
    isPromptExpanded: boolean;
    isReturningToProgress: boolean;
    feedbackState: "up" | "down" | null;
    scrollTop: number;
  } | null>(null);
  const wasChatNearBottomRef = useRef(true);
  const [showNewOutputIndicator, setShowNewOutputIndicator] = useState(false);

  const isChatNearBottom = useCallback((threshold = 96) => {
    const scrollEl = chatScrollRef.current;
    if (!scrollEl) return true;
    return scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight < threshold;
  }, []);

  const updateNewOutputIndicator = useCallback(() => {
    const scrollEl = chatScrollRef.current;
    if (!scrollEl || selectedRunHistory) {
      setShowNewOutputIndicator(false);
      return;
    }
    const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight + 12;
    const nearBottom = isChatNearBottom();
    wasChatNearBottomRef.current = nearBottom;
    setShowNewOutputIndicator(hasOverflow && !nearBottom);
  }, [isChatNearBottom, selectedRunHistory]);

  const scrollChatToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    wasChatNearBottomRef.current = true;
    setShowNewOutputIndicator(false);
    const scrollEl = chatScrollRef.current;
    if (scrollEl) {
      scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior });
      return;
    }
    chatBottomRef.current?.scrollIntoView({ block: "end", behavior });
  }, []);

  // ******************************************************************
  // Voice input state
  // ******************************************************************
  const [inputValue, setInputValue] = useState("");
  const [submittedPrompt, setSubmittedPrompt] = useState(""); // Store the last submitted prompt for display
  const [isPromptExpanded, setIsPromptExpanded] = useState(false); // Track if prompt bubble is expanded
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editingPromptText, setEditingPromptText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  const POLL_INTERVAL_BASE = 5000; // ms
  const PROMPT_RESPONSE_WIDTH = "70%";

  useEffect(() => {
    // Check for browser support and handle the prefix
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.error("Speech recognition not supported in this browser.");
    } else {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false; // Stops when you stop talking
      recognitionRef.current.interimResults = false;

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        setIsListening(false);
      };

      recognitionRef.current.onend = () => setIsListening(false);
      recognitionRef.current.onerror = () => setIsListening(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRunHistory) {
      setShowNewOutputIndicator(false);
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const scrollEl = chatScrollRef.current;
      if (!scrollEl) return;

      const hasOverflow = scrollEl.scrollHeight > scrollEl.clientHeight + 12;
      if (!hasOverflow) {
        setShowNewOutputIndicator(false);
        return;
      }

      if (wasChatNearBottomRef.current) {
        scrollChatToBottom("auto");
      } else {
        setShowNewOutputIndicator(true);
      }
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    chatTranscript,
    finalResult,
    artifacts.length,
    liveChatArtifacts.length,
    isPolling,
    isRunning,
    runStatus?.output,
    selectedRunHistory,
    scrollChatToBottom,
  ]);

  const handleMicClick = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      setIsListening(true);
      recognitionRef.current?.start();
    }
  };

  // ******************************************************************
  // ******************************************************************

  // Fetch agent data on mount
  useEffect(() => {
    let poller: any;
    let isUnmounted = false;
    const fetchAgent = async () => {
      try {
        const data = await getAgent(agentId);
        if (!isUnmounted) {
          setAgent(data);
          setError("");
          // Only poll every 5s if status is 'creating' or 'building'
          if (data.status === "creating" || data.status === "building") {
            poller = setTimeout(fetchAgent, POLL_INTERVAL_BASE);
          }
          // No polling for other statuses
        }
      } catch {
        if (!isUnmounted) setError("Failed to load agent details.");
      }
    };
    fetchAgent();
    return () => {
      isUnmounted = true;
      if (poller) clearTimeout(poller);
    };
  }, [agentId]);
  // Use agent object directly (API now returns JSON)
  const agentCard = agent;

  React.useEffect(() => {
    setInputValue("")
  }, []);

  // Fetch pinned prompts
  useEffect(() => {
    if (!agentId) return;
    const fetchPinnedPrompts = async () => {
      try {
        const data = await listPinnedPromptsForAgent(agentId);
        setPinnedPrompts(data);
      } catch (err) {
        console.error("Failed to fetch pinned prompts", err);
        setPinnedPrompts([]);
      }
    };
    fetchPinnedPrompts();
  }, [agentId, runsRefreshToken]);

  // Load historical run data when a run is selected from history
  useEffect(() => {
    if (!selectedRunHistory || !agentId) return;

    const loadHistoricalRun = async () => {
      setIsLoadingHistorical(true);
      setHistoricalChatSessionId(null);
      setHistoricalThreadNotice("");
      setArtifacts([]);
      try {
        const sessionId = selectedRunHistory.chat_session_id
          ? String(selectedRunHistory.chat_session_id)
          : "";
        const messagesPromise = sessionId
          ? listAgentChatMessages(agentId, sessionId).catch((err: any) => {
              console.error("Failed to load historical chat thread:", err);
              return null;
            })
          : Promise.resolve(null);

        // Fetch prompt, result, artifacts, and chat thread for the selected run.
        const [promptData, resultData, artifactsData, messagesData] = await Promise.all([
          getAgentRunPrompt(
            selectedRunHistory.agent_id,
            selectedRunHistory.agent_version,
            selectedRunHistory.run_number,
          ),
          getAgentRunResult(
            selectedRunHistory.agent_id,
            selectedRunHistory.agent_version,
            selectedRunHistory.run_number,
          ),
          getAgentRunArtifacts(
            selectedRunHistory.agent_id,
            selectedRunHistory.agent_version,
            selectedRunHistory.run_number,
          ),
          messagesPromise,
        ]);

        const loadedPrompt = parsePrompt(promptData?.prompt || "");
        const loadedResult = typeof resultData?.output === "string"
          ? resultData.output
          : resultData?.output
            ? JSON.stringify(resultData.output, null, 2)
            : "";
        setHistoricalPrompt(loadedPrompt);
        setHistoricalResult(loadedResult);

        // Transform artifacts to ensure they have all required fields
        const transformedArtifacts = await Promise.all(
          (artifactsData?.artifacts || []).map(async (art: any, index: number) => {
            const normalized = normalizeRunArtifact(
              art,
              {
                agentId: selectedRunHistory.agent_id,
                agentVersion: selectedRunHistory.agent_version,
                runNumber: selectedRunHistory.run_number,
              },
              index,
            );
            if (!isImageArtifact(normalized)) {
              return normalized;
            }
            const previewUrl = await fetchSecureImage(normalized.url);
            return previewUrl ? { ...normalized, previewUrl } : normalized;
          }),
        );
        setArtifacts(transformedArtifacts);

        if (sessionId && messagesData?.messages) {
          const transcript = buildTranscriptFromChatMessages(
            messagesData.messages,
            sessionId,
            selectedRunHistory,
            transformedArtifacts,
            loadedPrompt,
            loadedResult,
          );
          setChatTranscript(transcript);
          setHistoricalChatSessionId(sessionId);
          chatSessionIdRef.current = sessionId;
          setHistoricalThreadNotice("Viewing saved thread. Send a message to continue it.");
        } else {
          setChatTranscript([]);
          chatSessionIdRef.current = null;
          setHistoricalThreadNotice(
            "This older run can be viewed, but it does not have a resumable chat thread.",
          );
        }
      } catch (err) {
        console.error("Failed to load historical run:", err);
        setHistoricalPrompt("Failed to load prompt");
        setHistoricalResult("Failed to load result");
        setArtifacts([]);
        setChatTranscript([]);
        setHistoricalThreadNotice("Could not load this run cleanly.");
      } finally {
        setIsLoadingHistorical(false);
      }
    };

    loadHistoricalRun();
  }, [selectedRunHistory, agentId, runsRefreshToken]);

  // ****************************
  // Call the enhance prompt API
  // ****************************
  const enhancePromptApi = async (): Promise<string> => {
    try {
      const response = await enhancePrompt({
        role: agentCard?.role,
        description: agentCard?.description,
        input_prompt: inputValue,
        tools: agentCard?.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      return response.enhanced_prompt;
    } catch (error) {
      console.error("Error enhancing prompt:", error);
      throw new Error("Failed to enhance prompt. Please try again.");
    }
  };

  const refreshRunHistory = useCallback(async () => {
    if (!agentId) return [];
    const response = await (listAllAgentRuns as any)(agentId, {
      page: 1,
      page_size: historyPageSize,
      version: null,
      triggered_by_workflow: false,
    });
    const runs: any[] = response.runs || [];
    setAgentRuns(runs);
    setTotalHistoryRuns(response.total || 0);
    setHistoryPage(1);
    setHasMoreHistory(runs.length < (response.total || 0));
    setSelectedRunHistory((current: any) => {
      if (!current) return current;
      const currentVersion = current.agent_version ?? current.version;
      const updated = runs.find((run) =>
        run.run_number === current.run_number &&
        String(run.agent_version ?? run.version ?? "") === String(currentVersion ?? ""),
      );
      if (!updated) return current;
      const unchanged =
        normalizeRunStatus(updated.status) === normalizeRunStatus(current.status) &&
        String(updated.updated_at ?? "") === String(current.updated_at ?? "") &&
        String(updated.run_label ?? "") === String(current.run_label ?? "") &&
        Boolean(updated.favorite) === Boolean(current.favorite) &&
        String(updated.chat_message_id ?? "") === String(current.chat_message_id ?? "") &&
        String(updated.chat_session_id ?? "") === String(current.chat_session_id ?? "");
      return unchanged ? current : { ...current, ...updated };
    });
    return runs;
  }, [agentId, historyPageSize]);

  // Fetch Agent Runs for History (initial load)
  useEffect(() => {
    if (!agentId) return;
    let isMounted = true;
    const fetchRuns = async () => {
      try {
        const runs = await refreshRunHistory();
        if (!isMounted) return;

        // Auto-resume polling if the most recent run is still in-progress.
        // This handles the case where the user navigated away mid-run and came back.
        // Only resume once per component mount (hasAutoResumedRef prevents double-fire).
        const latestRun = runs[0];
        console.debug("[Resume] hasAutoResumed:", hasAutoResumedRef.current,
          "latestRun:", latestRun ?
          { status: latestRun.status, run_number: latestRun.run_number,
            agent_version: latestRun.agent_version } : null);
        if (
          !hasAutoResumedRef.current &&
          latestRun &&
          isActiveRunStatus(latestRun.status)
        ) {
          hasAutoResumedRef.current = true;
          console.debug("[Resume] Detected in-progress run — starting resume polling", latestRun.status);
          const resultEndpoint = `/agent/${agentId}/version/${latestRun.agent_version}/runs/${latestRun.run_number}/result`;

          // Restore the prompt bubble if available
          if (latestRun.agent_prompt) {
            // agent_prompt is stored as JSON string e.g. '{"input":"..."}' — extract the inner text
            try {
              const parsed = JSON.parse(latestRun.agent_prompt);
              setSubmittedPrompt(parsed.input ?? latestRun.agent_prompt);
            } catch {
              setSubmittedPrompt(latestRun.agent_prompt);
            }
          }
          setHasRunTriggered(true);
          setIsPolling(true);
          setIsReturningToProgress(true);
          setFinalResult("");

          dotsIntervalRef.current = setInterval(() => {
            if (!isMounted) return;
            setThinkingDots((prev) => (prev + 1) % 4);
          }, 500);

          const stopPolling = () => {
            if (!isMounted) return;
            setIsPolling(false);
            setIsRunning(false);
            setThinkingDots(0);
            if (dotsIntervalRef.current) {
              clearInterval(dotsIntervalRef.current);
              dotsIntervalRef.current = null;
            }
          };

          const resumePoll = async () => {
            if (!isMounted) return;
            try {
              const data = (await getAgentRunResultFromUrl(resultEndpoint)) as any;
              if (!isMounted) return;
              if (data.status === "success") {
                stopPolling();
                const output = typeof data.output === "string" ? data.output.trim() : data.output ? JSON.stringify(data.output, null, 2) : "";
                setFinalResult(output);
                markRunInHistory(latestRun.run_number, "completed");
                setRunsRefreshToken((prev) => prev + 1);
                return;
              }
              if (data.status === "error" || data.status === "failed") {
                stopPolling();
                markRunInHistory(latestRun.run_number, "failed");
                setRunStatus({ status: "error", message: data.message || "Agent execution failed", output: data.output });
                return;
              }
            } catch (err) {
              if (!isMounted) return;
              console.error("Error during resumed polling:", err);
            }
            if (isMounted) {
              pollTimeoutRef.current = setTimeout(resumePoll, 2000);
            }
          };

          resumePoll();
        }
      } catch (err) {
        if (isMounted) console.error("Failed to fetch history", err);
      }
    };
    fetchRuns();
    return () => {
      isMounted = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      if (dotsIntervalRef.current) {
        clearInterval(dotsIntervalRef.current);
        dotsIntervalRef.current = null;
      }
    };
  }, [agentId, runsRefreshToken, refreshRunHistory]);

  useEffect(() => {
    if (!agentId || !agentRuns.some((run) => isActiveRunStatus(run.status))) return;
    const interval = window.setInterval(() => {
      refreshRunHistory().catch((err) => {
        console.error("Failed to refresh active run history", err);
      });
    }, 2000);
    return () => window.clearInterval(interval);
  }, [agentId, agentRuns, refreshRunHistory]);

  // Load more history runs
  const handleLoadMoreHistory = async () => {
    if (!agentId || isLoadingMoreHistory || !hasMoreHistory) return;

    setIsLoadingMoreHistory(true);
    try {
      const nextPage = historyPage + 1;
      const response = await (listAllAgentRuns as any)(agentId, {
        page: nextPage,
        page_size: historyPageSize,
        version: null,
        triggered_by_workflow: false,
      });

      const newRuns = response.runs || [];
      setAgentRuns((prev) => [...prev, ...newRuns]);
      setHistoryPage(nextPage);

      // Check if there are more runs to load
      const totalLoaded = agentRuns.length + newRuns.length;
      setHasMoreHistory(totalLoaded < (response.total || 0));
    } catch (err) {
      console.error("Failed to load more history", err);
    } finally {
      setIsLoadingMoreHistory(false);
    }
  };

  const handleCopyPrompt = (text: string) => {
    setInputValue(text);
  };

  const copyTextToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarOpen(true);
      setTimeout(() => setSnackbarOpen(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const buildRunDeepLink = (
    runNumber?: number | null,
    agentVersion?: number | string | null,
    messageId?: string,
  ) => {
    const url = new URL(window.location.href);
    if (runNumber) {
      url.searchParams.set("run", String(runNumber));
    }
    if (agentVersion) {
      url.searchParams.set("version", String(agentVersion));
    }
    if (messageId) {
      url.hash = `message-${messageId}`;
    }
    return url.toString();
  };

  const openShareDialog = ({
    kind,
    text,
    title,
    runNumber,
    agentVersion,
    messageId,
  }: {
    kind: SharePayload["kind"];
    text: string;
    title?: string;
    runNumber?: number | null;
    agentVersion?: number | string | null;
    messageId?: string;
  }) => {
    const trimmed = String(text || "").trim();
    setShareCopied(false);
    setShareDialog({
      kind,
      title: title || agent?.display_name || "Next Moca run",
      text: trimmed.length > 520 ? `${trimmed.slice(0, 520)}...` : trimmed,
      url: buildRunDeepLink(runNumber, agentVersion ?? agent?.version, messageId),
    });
  };

  const copyShareLink = async () => {
    if (!shareDialog) return;
    await copyTextToClipboard(shareDialog.url);
    setShareCopied(true);
  };

  const shareNativeOrCopy = async () => {
    if (!shareDialog) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareDialog.title,
          text: shareDialog.text,
          url: shareDialog.url,
        });
        return;
      } catch (err) {
        // User cancellation should not surface as an error in the run UI.
        console.debug("Native share dismissed:", err);
      }
    }
    await copyShareLink();
  };

  const openShareTarget = (target: "x" | "linkedin" | "reddit") => {
    if (!shareDialog) return;
    const encodedUrl = encodeURIComponent(shareDialog.url);
    const encodedTitle = encodeURIComponent(shareDialog.title);
    const encodedText = encodeURIComponent(`${shareDialog.text}\n${shareDialog.url}`);
    const targetUrl =
      target === "x"
        ? `https://twitter.com/intent/tweet?text=${encodedText}`
        : target === "linkedin"
          ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`
          : `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`;
    window.open(targetUrl, "_blank", "noopener,noreferrer");
  };

  const beginPromptEdit = (message: ChatTranscriptItem) => {
    if (isRunning || isPolling) return;
    setEditingPromptId(message.id);
    setEditingPromptText(message.content);
  };

  const cancelPromptEdit = () => {
    setEditingPromptId(null);
    setEditingPromptText("");
  };

  const savePromptEdit = (): string | null => {
    const nextText = editingPromptText.trim();
    if (!editingPromptId || !nextText) return null;
    setChatTranscript((prev) =>
      prev.map((item) =>
        item.id === editingPromptId ? { ...item, content: nextText } : item,
      ),
    );
    cancelPromptEdit();
    return nextText;
  };

  const rerunPrompt = async (promptText: string) => {
    const nextText = promptText.trim();
    if (!nextText || isRunning || isPolling) return;
    await handleRun(nextText, { useSelectedFiles: false });
  };

  const rerunEditedPrompt = async () => {
    const nextText = savePromptEdit();
    if (nextText) {
      await rerunPrompt(nextText);
    }
  };

  const parseAgentArtifactHref = (href?: string) => {
    if (!href) return null;
    const match = href.match(
      /^\/agent\/([^/]+)\/version\/([^/]+)\/runs\/([^/]+)\/artifact\/(.+)$/,
    );
    if (!match) return null;
    return {
      agent_id: match[1],
      agent_version: Number(match[2]),
      run_number: Number(match[3]),
      filename: decodeURIComponent(match[4]),
    };
  };

  const handleClearInput = () => {
    setInputValue("");
  };

  const handleDeletePrompt = async (promptId: any) => {
    setError("");
    if (!window.confirm(`Delete prompt '${promptId}'? This cannot be undone.`))
      return;
    setDeleting(promptId);
    try {
      await deletePinnedPromptForAgent(agentId, promptId);
      setPinnedPrompts(pinnedPrompts.filter((p: any) => p.id !== promptId));
    } catch (e) {
      setError("Failed to delete prompt.");
    } finally {
      setDeleting("");
    }
  };

  const handleArtifactDownload = async (artifact: any) => {
    try {
      // Use the proper API endpoint like AgentRuns does
      if (
        !artifact.agent_id ||
        !artifact.agent_version ||
        !artifact.run_number ||
        !artifact.filename
      ) {
        console.error("Artifact missing required fields:", artifact);
        return;
      }

      console.log("Downloading artifact:", {
        agent_id: artifact.agent_id,
        agent_version: artifact.agent_version,
        run_number: artifact.run_number,
        filename: artifact.filename,
      });

      const response = await downloadArtifactFile(
        artifact.agent_id,
        artifact.agent_version,
        artifact.run_number,
        artifact.filename,
      );

      if (!response || !response.ok) {
        throw new Error(`Failed to fetch: ${response?.statusText}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", artifact.filename);
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      alert(`Download failed for ${artifact.filename}.`);
    }
  };

  const markdownComponents = {
    a: ({ href, children, ...props }: any) => {
      const artifact = parseAgentArtifactHref(href);
      if (artifact) {
        return (
          <Box
            component="button"
            type="button"
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              handleArtifactDownload(artifact);
            }}
            sx={{
              border: 0,
              p: 0,
              m: 0,
              bgcolor: "transparent",
              color: "#9A5A24",
              cursor: "pointer",
              font: "inherit",
              fontWeight: 700,
              textDecoration: "underline",
              textUnderlineOffset: "3px",
              "&:hover": { color: "#B87333" },
            }}
          >
            {children}
          </Box>
        );
      }
      return (
        <a href={href} target="_blank" rel="noreferrer" {...props}>
          {children}
        </a>
      );
    },
  };

  const InlineImageArtifacts = ({ artifacts: messageArtifacts }: { artifacts?: any[] }) => {
    const imageArtifacts = (messageArtifacts || []).filter(isImageArtifact);
    if (imageArtifacts.length === 0) return null;

    return (
      <Stack spacing={1.5} sx={{ mt: 1.5 }}>
        {imageArtifacts.map((artifact) => {
          const imageSrc =
            artifact.previewUrl ||
            artifact.thumbnail ||
            (String(artifact.url || "").startsWith("blob:") || String(artifact.url || "").startsWith("data:")
              ? artifact.url
              : undefined);
          return (
            <Box key={artifactIdentity(artifact)} sx={{ maxWidth: "100%" }}>
              {imageSrc ? (
                <Box
                  component="img"
                  src={imageSrc}
                  alt={artifact.name || artifact.filename || "Generated image"}
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: { xs: 320, md: 460 },
                    borderRadius: 2,
                    border: `1px solid ${isDark ? "rgba(224,177,132,0.24)" : "rgba(184, 115, 51, 0.24)"}`,
                    objectFit: "contain",
                    bgcolor: contentSurface,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.25,
                    py: 1,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? "rgba(224,177,132,0.24)" : "rgba(184, 115, 51, 0.24)"}`,
                    color: copperText,
                    bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.05)",
                  }}
                >
                  <ImageIcon sx={{ fontSize: 18 }} />
                  <Typography variant="body2">Generated image is ready</Typography>
                </Box>
              )}
              <Box
                component="button"
                type="button"
                onClick={() => handleArtifactDownload(artifact)}
                sx={{
                  mt: 0.75,
                  p: 0,
                  border: 0,
                  bgcolor: "transparent",
                  color: copperText,
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: "0.8rem",
                  fontWeight: 700,
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                  "&:hover": { color: COPPER },
                }}
              >
                Download {artifact.name || artifact.filename || "image"}
              </Box>
            </Box>
          );
        })}
      </Stack>
    );
  };

  const ArtifactPendingIndicator = () => (
    <Box
      sx={{
        mt: 1.35,
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.25,
        py: 0.85,
        borderRadius: 2,
        border: `1px solid ${isDark ? "rgba(224,177,132,0.24)" : "rgba(184, 115, 51, 0.24)"}`,
        color: copperText,
        bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.055)",
      }}
    >
      <CircularProgress size={15} thickness={5} sx={{ color: COPPER }} />
      <ImageIcon sx={{ fontSize: 17, color: copperText }} />
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
        Finalizing generated assets...
      </Typography>
    </Box>
  );

  const handleOutcomeFeedback = async (
    message: ChatTranscriptItem,
    feedback: "up" | "down",
    note?: string,
  ) => {
    const feedbackSessionId = message.sessionId || chatSessionIdRef.current;
    if (!agentId || !feedbackSessionId || !message.backendMessageId || message.feedbackPending) {
      return;
    }
    const nextFeedback = feedback;
    const previousFeedback = message.feedback || null;
    setChatTranscript((prev) =>
      prev.map((item) =>
        item.id === message.id
          ? { ...item, feedback: nextFeedback, feedbackPending: true }
          : item,
      ),
    );

    try {
      const response = await submitAgentOutcomeFeedback(
        agentId,
        feedbackSessionId,
        message.backendMessageId,
        {
          feedback: nextFeedback,
          prompt: message.prompt || submittedPrompt || "",
          response: message.content || "",
          artifacts: (message.artifacts || []).map((artifact) => ({
            name: artifact.name,
            filename: artifact.filename,
            type: artifact.type,
            run_number: artifact.run_number,
            agent_version: artifact.agent_version,
          })),
          note,
        },
      );
      const returnedFeedback = response?.message?.metadata?.outcome_feedback?.feedback;
      const confirmedFeedback: "up" | "down" =
        returnedFeedback === "up" || returnedFeedback === "down" ? returnedFeedback : nextFeedback;
      setChatTranscript((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? { ...item, feedback: confirmedFeedback, feedbackPending: false }
            : item,
        ),
      );
    } catch (err) {
      console.error("Failed to submit outcome feedback:", err);
      setChatTranscript((prev) =>
        prev.map((item) =>
          item.id === message.id
            ? { ...item, feedback: previousFeedback, feedbackPending: false }
            : item,
        ),
      );
    }
  };

  const OutcomeFeedbackToolbar = ({ message }: { message: ChatTranscriptItem }) => {
    if (
      message.role !== "assistant" ||
      message.status !== "completed" ||
      message.artifactsPending ||
      (!message.content.trim() && !(message.artifacts || []).some(isImageArtifact))
    ) {
      return null;
    }
    const disabled = !message.backendMessageId || !!message.feedbackPending;
    const iconSx = (active: boolean) => ({
      fontSize: "1rem",
      color: active ? COPPER_DARK : "#94A3B8",
    });
    return (
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        sx={{ mt: 1.1, ml: -0.5, opacity: 0.86 }}
      >
        <Tooltip title="Share result">
          <IconButton
            size="small"
            onClick={() =>
              openShareDialog({
                kind: "response",
                title: agent?.display_name || "Next Moca result",
                text: message.content || "Generated result",
                runNumber: message.runNumber,
                agentVersion: agent?.version,
                messageId: message.id,
              })
            }
            sx={{
              width: 28,
              height: 28,
              color: textMuted,
              "&:hover": { bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.08)", color: copperText },
            }}
          >
            <ShareIcon sx={iconSx(false)} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Favorite this result for Mnemo">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => handleOutcomeFeedback(message, "up", "favorite_result")}
              sx={{
                width: 28,
                height: 28,
                border: `1px solid ${message.feedback === "up" ? copperBorder : "transparent"}`,
                color: message.feedback === "up" ? copperText : textMuted,
                "&:hover": { bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.08)" },
              }}
            >
              {message.feedback === "up" ? (
                <FavoriteIcon sx={iconSx(true)} />
              ) : (
                <FavoriteBorderIcon sx={iconSx(false)} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Mark as desired result">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => handleOutcomeFeedback(message, "up", "positive_outcome")}
              sx={{
                width: 28,
                height: 28,
                border: `1px solid ${message.feedback === "up" ? copperBorder : "transparent"}`,
                color: message.feedback === "up" ? copperText : textMuted,
                "&:hover": { bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.08)" },
              }}
            >
              {message.feedback === "up" ? (
                <ThumbUpIcon sx={iconSx(true)} />
              ) : (
                <ThumbUpOutlinedIcon sx={iconSx(false)} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Mark as not useful">
          <span>
            <IconButton
              size="small"
              disabled={disabled}
              onClick={() => handleOutcomeFeedback(message, "down")}
              sx={{
                width: 28,
                height: 28,
                border: `1px solid ${message.feedback === "down" ? copperBorder : "transparent"}`,
                color: message.feedback === "down" ? copperText : textMuted,
                "&:hover": { bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.08)" },
              }}
            >
              {message.feedback === "down" ? (
                <ThumbDownIcon sx={iconSx(true)} />
              ) : (
                <ThumbDownOutlinedIcon sx={iconSx(false)} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="caption" sx={{ color: textMuted, fontSize: "0.72rem" }}>
          {message.feedbackPending
            ? "Saving signal..."
            : message.feedback === "up"
              ? "Learning from this result"
              : message.feedback === "down"
                ? "Marked for review"
                : "Outcome signal"}
        </Typography>
      </Stack>
    );
  };

  const authSignalKey = (signal: AuthRequiredSignal) =>
    `${signal.provider || "oauth"}:${signal.required_env || "runtime"}:${signal.tool_name || "tool"}`;

  const handleOAuthConnect = async (signal: AuthRequiredSignal, message: ChatTranscriptItem) => {
    if (!agentId || !signal?.provider) return;
    const connectionKey = authSignalKey(signal);
    setOauthConnectingKey(connectionKey);
    oauthRetryRef.current = {
      prompt: message.prompt || submittedPrompt || inputValue,
      agentVersion: agent?.version,
      runNumber: message.runNumber,
    };
    try {
      const response = await startOAuthConnect({
        provider: signal.provider,
        required_env: signal.required_env,
        tool_name: signal.tool_name,
        scopes: signal.scopes,
        agent_id: agentId,
        agent_version: agent?.version,
        run_number: message.runNumber,
        return_to: window.location.href,
      });
      const authUrl = response?.authorization_url;
      if (!authUrl) {
        throw new Error("OAuth provider did not return an authorization URL");
      }
      const popup = window.open(
        authUrl,
        "nextmoca_oauth_connect",
        "width=640,height=760,menubar=no,toolbar=no,location=no,status=no",
      );
      if (!popup) {
        throw new Error("Popup blocked. Allow popups for Next Moca and try again.");
      }
    } catch (err: any) {
      console.error("OAuth connect failed:", err);
      setRunStatus({ status: "error", message: err?.message || "Could not start OAuth connection." });
      setSnackbarOpen(true);
      oauthRetryRef.current = null;
      setOauthConnectingKey(null);
    }
  };

  React.useEffect(() => {
    const onOAuthMessage = (event: MessageEvent) => {
      const payload = event.data || {};
      if (payload.type !== "nextmoca_oauth_complete") return;
      const retry = oauthRetryRef.current;
      oauthRetryRef.current = null;
      setOauthConnectingKey(null);
      if (payload.status !== "success") {
        setRunStatus({ status: "error", message: payload.message || "OAuth connection was not completed." });
        setSnackbarOpen(true);
        return;
      }
      if (retry?.prompt) {
        handleRunRef.current?.(retry.prompt, { useSelectedFiles: false });
      }
    };
    window.addEventListener("message", onOAuthMessage);
    return () => window.removeEventListener("message", onOAuthMessage);
  }, [agentId]);

  const AuthRequiredConnectCard = ({ message }: { message: ChatTranscriptItem }) => {
    const signals = message.authRequired || [];
    if (!signals.length) return null;
    return (
      <Stack spacing={1} sx={{ mt: 1.25 }}>
        {signals.map((signal) => {
          const key = authSignalKey(signal);
          const busy = oauthConnectingKey === key;
          return (
            <Paper
              key={key}
              elevation={0}
              sx={{
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${copperBorder}`,
                bgcolor: isDark ? "rgba(224,177,132,0.12)" : "rgba(184, 115, 51, 0.055)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: textPrimary }}>
                    Connect {signal.provider_label || signal.provider || "provider"} to continue
                  </Typography>
                  <Typography variant="caption" sx={{ color: textMuted }}>
                    Required credential: {signal.required_env || "runtime OAuth token"}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={() => handleOAuthConnect(signal, message)}
                  startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <LinkIcon />}
                  sx={{
                    borderRadius: 999,
                    bgcolor: COPPER,
                    color: "white",
                    fontWeight: 800,
                    textTransform: "none",
                    px: 1.75,
                    "&:hover": { bgcolor: isDark ? "#E0B184" : COPPER_DARK },
                    "&.Mui-disabled": { bgcolor: "rgba(184, 115, 51, 0.22)", color: "#9CA3AF" },
                  }}
                >
                  {busy ? "Connecting..." : `Connect ${signal.provider_label || signal.provider || ""}`.trim()}
                </Button>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    );
  };

  const regeneratePrompt = async () => {
    if (!agent) return;
    setIsEnhancing(true);
    try {
      const result = await enhancePrompt({
        role: agent.role,
        description: agent.description,
        input_prompt: inputValue,
        tools: agent.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      setEnhancedSuggestion(result.enhanced_prompt);
    } catch (err) {
      console.error("Failed to regenerate:", err);
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleEnhanceClick = async () => {
    setIsModalOpen(true);
    await regeneratePrompt();
  };

  const handleAcceptEnhancement = (finalPrompt: string) => {
    setInputValue(finalPrompt)
    setIsModalOpen(false); // Close modal
  };

  const handleRejectEnhancement = () => {
    setIsModalOpen(false); // Close modal
  };

  // ******************************************************************
  // File attachment in prompt
  // ******************************************************************
  const [selectedFiles, setSelectedFiles] = useState<FileReference[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterText, setFilterText] = useState("");
  const [fileLibrary, setFileLibrary] = useState<FileReference[]>([]);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(false);
  const [hasLoadedArtifacts, setHasLoadedArtifacts] = useState(false);
  const [loadedRunsCount, setLoadedRunsCount] = useState(0);
  const [totalRunsCount, setTotalRunsCount] = useState(0);
  const [hasMoreRuns, setHasMoreRuns] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set()); // Track all created blob URLs

  const captureLiveChatSnapshot = useCallback(() => {
    if (selectedRunHistory || liveChatSnapshotRef.current) return;
    liveChatSnapshotRef.current = {
      chatSessionId: chatSessionIdRef.current,
      chatTranscript,
      artifacts,
      liveChatArtifacts,
      inputValue,
      submittedPrompt,
      selectedFiles,
      hasRunTriggered,
      finalResult,
      runStatus,
      isPromptExpanded,
      isReturningToProgress,
      feedbackState,
      scrollTop: chatScrollRef.current?.scrollTop || 0,
    };
  }, [
    artifacts,
    chatTranscript,
    feedbackState,
    finalResult,
    hasRunTriggered,
    inputValue,
    isReturningToProgress,
    isPromptExpanded,
    liveChatArtifacts,
    runStatus,
    selectedFiles,
    selectedRunHistory,
    submittedPrompt,
  ]);

  const restoreLiveChatSnapshot = useCallback(() => {
    const snapshot = liveChatSnapshotRef.current;
    if (!snapshot) return false;

    chatSessionIdRef.current = snapshot.chatSessionId;
    setChatTranscript(snapshot.chatTranscript);
    setArtifacts(snapshot.artifacts);
    setLiveChatArtifacts(snapshot.liveChatArtifacts);
    setInputValue(snapshot.inputValue);
    setSubmittedPrompt(snapshot.submittedPrompt);
    setSelectedFiles(snapshot.selectedFiles);
    setHasRunTriggered(snapshot.hasRunTriggered);
    setFinalResult(snapshot.finalResult);
    setRunStatus(snapshot.runStatus);
    setIsPromptExpanded(snapshot.isPromptExpanded);
    setFeedbackState(snapshot.feedbackState);
    setIsReturningToProgress(snapshot.isReturningToProgress);
    liveChatSnapshotRef.current = null;

    window.requestAnimationFrame(() => {
      if (chatScrollRef.current) {
        chatScrollRef.current.scrollTop = snapshot.scrollTop;
      }
    });

    return true;
  }, []);

  const handleSelectHistoricalRun = useCallback((run: any) => {
    captureLiveChatSnapshot();
    setSelectedRunHistory(run);
    setSelectedRunId(run.id);
    setIsHistoricalPromptExpanded(false);
    setEditingPromptId(null);
    setEditingPromptText("");
  }, [captureLiveChatSnapshot]);

  // Fetch all artifacts from all runs when user first types @file
  // Helper function to fetch image securely as blob (prevents token leakage in logs/history)
  const fetchSecureImage = async (url?: string): Promise<string | undefined> => {
    try {
      if (!url) return undefined;
      const token = getAuthToken();
      const response = await fetch(url.startsWith("http") ? url : `${API_BASE}${url}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(`Failed to fetch image: ${response.status}`);
        return undefined;
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Track this blob URL for cleanup on unmount
      blobUrlsRef.current.add(blobUrl);

      return blobUrl;
    } catch (error) {
      console.error("Failed to fetch secure image:", error);
      return undefined;
    }
  };

  const INITIAL_RUNS_LIMIT = 20;
  const LOAD_MORE_INCREMENT = 20; // Load 20 more runs when user clicks "Load More"
  const CONCURRENCY_LIMIT = 20; // Process 20 runs concurrently

  const fetchAllArtifacts = async (append: boolean = false) => {
    if (isLoadingArtifacts || !agentId) return;

    setIsLoadingArtifacts(true);
    try {
      // Calculate how many runs to fetch
      const limit = append ? LOAD_MORE_INCREMENT : INITIAL_RUNS_LIMIT;
      const offset = append ? loadedRunsCount : 0;

      // Fetch one page to get the most recent runs
      const runsResponse = await (listAllAgentRuns as any)(agentId, {
        page: Math.floor(offset / 200) + 1,
        page_size: 200,
        version: null,
        triggered_by_workflow: false,
      });

      const allRuns = runsResponse.runs || [];
      const totalRuns = runsResponse.total || 0;

      // Slice to only get the runs we want (most recent first)
      const runsToProcess = allRuns.slice(
        offset % 200,
        Math.min((offset % 200) + limit, allRuns.length),
      );

      setTotalRunsCount(totalRuns);
      const newLoadedCount = offset + runsToProcess.length;
      setLoadedRunsCount(newLoadedCount);
      setHasMoreRuns(newLoadedCount < totalRuns);

      // Helper function to check if file is an image
      const isImageFile = (filename: string) => {
        const imageExtensions = [
          ".png",
          ".jpg",
          ".jpeg",
          ".gif",
          ".bmp",
          ".webp",
          ".svg",
        ];
        return imageExtensions.some((ext) =>
          filename.toLowerCase().endsWith(ext),
        );
      };

      // Helper function to process a single run's artifacts
      const processRunArtifacts = async (
        run: any,
      ): Promise<FileReference[]> => {
        try {
          const response = await getAgentRunArtifacts(
            agentId,
            run.agent_version,
            run.run_number,
          );

          const artifacts = response?.artifacts || [];
          if (artifacts.length === 0) return [];

          // Fetch thumbnails concurrently for all images in this run
          const artifactPromises = artifacts.map(async (artifact: any) => {
            const thumbnail = isImageFile(artifact.filename)
              ? await fetchSecureImage(artifact.url)
              : undefined;

            return {
              id: `${run.agent_version}-${run.run_number}-${artifact.filename}`,
              name: artifact.filename,
              version: `${run.agent_version}`,
              runNumber: run.run_number,
              thumbnail: thumbnail,
            };
          });

          return await Promise.all(artifactPromises);
        } catch (err) {
          console.error(
            `Failed to fetch artifacts for run ${run.run_number}:`,
            err,
          );
          return [];
        }
      };

      // Fetch artifacts with bounded concurrency
      const newArtifacts: FileReference[] = [];

      for (let i = 0; i < runsToProcess.length; i += CONCURRENCY_LIMIT) {
        const batch = runsToProcess.slice(i, i + CONCURRENCY_LIMIT);
        const batchResults = await Promise.all(
          batch.map((run: any) => processRunArtifacts(run)),
        );

        // Flatten and add to results
        batchResults.forEach((artifacts) => {
          newArtifacts.push(...artifacts);
        });
      }

      // Append to existing or replace
      if (append) {
        setFileLibrary((prev) => [...prev, ...newArtifacts]);
      } else {
        setFileLibrary(newArtifacts);
        setHasLoadedArtifacts(true);
      }
    } catch (error) {
      console.error("Failed to fetch artifacts:", error);
      if (!append) {
        setFileLibrary([]);
        setHasLoadedArtifacts(true);
      }
    } finally {
      setIsLoadingArtifacts(false);
    }
  };

  // Load more artifacts when user clicks "Load More"
  const loadMoreArtifacts = () => {
    fetchAllArtifacts(true);
  };
  // Sync selectedFiles with promptText to prevent orphaned file references
  React.useEffect(() => {
    // Remove files from selectedFiles if their names are no longer in the prompt
    setSelectedFiles((current) =>
      current.filter((file) => inputValue.includes(file.name)),
    );
  }, [inputValue]);

  // Cleanup blob URLs ONLY on component unmount
  React.useEffect(() => {
    return () => {
      // Revoke all blob URLs to prevent memory leaks
      blobUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      blobUrlsRef.current.clear();
    };
  }, []); // Empty dependency array = only runs on unmount

  // Handle @file input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const val = e.target.value;
    setInputValue(val);

    const matches = Array.from(val.matchAll(/@file(\w*)/g));
    const match = matches.length > 0 ? matches[matches.length - 1] : null;
    if (match) {
      setFilterText(match[1] || "");
      setAnchorEl(inputRef.current);

      if (!hasLoadedArtifacts && !isLoadingArtifacts) {
        fetchAllArtifacts();
      }
    } else {
      setAnchorEl(null);
    }
  };

  const handleFileSelect = (file: FileReference) => {
    const lastTriggerIndex = inputValue.lastIndexOf("@file");
    if (lastTriggerIndex !== -1) {
      // 1. Always replace @file with the filename in the text
      const before = inputValue.substring(0, lastTriggerIndex);
      const newinputValue = `${before}${file.name} `;
      setInputValue(newinputValue);

      // 2. Track metadata - only add if not already present (prevent duplicate chips)
      // The useEffect will clean up selectedFiles automatically
      if (!selectedFiles.find((f) => f.id === file.id)) {
        setSelectedFiles((prev) => [...prev, file]);
        console.log(`Added file ${file.name} to selected files`);
      } else {
        console.log(
          `File ${file.name} already in selected files - only updating text`,
        );
      }
    }
    setAnchorEl(null);
  };
  const filteredOptions = fileLibrary.filter((f) =>
    f.name.toLowerCase().includes(filterText.toLowerCase()),
  );

  useEffect(() => {
    if (selectedRunHistory || agentRuns.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const runParam = params.get("run");
    const versionParam = params.get("version");
    if (!runParam) return;
    const linkedRun = agentRuns.find((run) => {
      const runVersion = run.agent_version ?? run.version;
      return (
        String(run.run_number) === runParam &&
        (!versionParam || String(runVersion) === versionParam)
      );
    });
    if (linkedRun) {
      handleSelectHistoricalRun(linkedRun);
      setIsHistoryOpen(true);
    }
  }, [agentRuns, handleSelectHistoricalRun, selectedRunHistory]);

  if (!agent) {
    return (
      <Box sx={{ px: PAGE_SHELL.paddingX, py: PAGE_SHELL.paddingY, maxWidth: PAGE_SHELL.maxWidth, mx: "auto" }}>
        <Typography>Loading agent...</Typography>
      </Box>
    );
  }

  // ******************************************************************

  const OnRunCompleted = () => {
    // Clear polling timeout
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }

    // Clear thinking dots interval
    if (dotsIntervalRef.current) {
      clearInterval(dotsIntervalRef.current);
      dotsIntervalRef.current = null;
    }

    // Reset polling state
    setIsPolling(false);
    setIsRunning(false);
    setThinkingDots(0);
  };

  const markRunInHistory = (runNumber: number | undefined, status: string) => {
    if (!runNumber) return;
    setAgentRuns((current) =>
      current.map((run) =>
        run.run_number === runNumber ? { ...run, status } : run,
      ),
    );
    setSelectedRunHistory((current: any) =>
      current?.run_number === runNumber ? { ...current, status } : current,
    );
  };

  const getRunKey = (run: any) =>
    `${run?.agent_version ?? run?.version ?? agent?.version ?? "v"}:${run?.run_number ?? "run"}`;

  const updateRunFavoriteState = (run: any, favorite: boolean) => {
    const key = getRunKey(run);
    setAgentRuns((current) =>
      current.map((item) => (getRunKey(item) === key ? { ...item, favorite } : item)),
    );
    setSelectedRunHistory((current: any) =>
      current && getRunKey(current) === key ? { ...current, favorite } : current,
    );
  };

  const setRunFavoritePending = (run: any, pending: boolean) => {
    const key = getRunKey(run);
    setFavoriteRunPendingKeys((current) => {
      const next = new Set(current);
      if (pending) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const buildRunFeedbackPayload = async (run: any) => {
    const agentVersion = run?.agent_version ?? run?.version ?? agent?.version;
    const runNumber = run?.run_number;
    let promptText = parsePrompt(run?.agent_prompt || "");
    let responseText = "";
    let runArtifacts: any[] = [];

    if (selectedRunHistory && getRunKey(selectedRunHistory) === getRunKey(run)) {
      promptText = historicalPrompt || promptText;
      responseText = historicalResult || "";
      runArtifacts = artifacts || [];
    }

    if ((!responseText || runArtifacts.length === 0) && agentId && agentVersion && runNumber) {
      try {
        const [resultData, artifactsData] = await Promise.all([
          responseText ? Promise.resolve(null) : getAgentRunResult(agentId, agentVersion, runNumber),
          runArtifacts.length > 0
            ? Promise.resolve(null)
            : getAgentRunArtifacts(agentId, agentVersion, runNumber),
        ]);
        if (!responseText && resultData) {
          const output = resultData?.output;
          responseText =
            typeof output === "string"
              ? output
              : output
                ? JSON.stringify(output, null, 2)
                : "";
        }
        if (runArtifacts.length === 0 && artifactsData?.artifacts) {
          runArtifacts = artifactsData.artifacts.map((artifact: any, index: number) =>
            normalizeRunArtifact(
              artifact,
              { agentId: agentId!, agentVersion, runNumber },
              index,
            ),
          );
        }
      } catch (err) {
        console.error("Failed to build favorite payload for run:", err);
      }
    }

    return {
      feedback: "up" as const,
      note: "favorite_run",
      prompt: promptText,
      response: responseText,
      artifacts: (runArtifacts || []).map((artifact) => ({
        name: artifact.name,
        filename: artifact.filename,
        type: artifact.type,
        run_number: artifact.run_number ?? runNumber,
        agent_version: artifact.agent_version ?? agentVersion,
      })),
    };
  };

  const favoriteRun = async (run: any) => {
    if (!agentId || run?.favorite) return;
    const sessionId = run?.chat_session_id;
    const messageId = run?.chat_message_id;
    if (!sessionId || !messageId) {
      setError("This run was created before chat-memory feedback was available, so it cannot be favorited into Mnemo.");
      return;
    }

    setRunFavoritePending(run, true);
    updateRunFavoriteState(run, true);
    try {
      const payload = await buildRunFeedbackPayload(run);
      await submitAgentOutcomeFeedback(agentId, sessionId, messageId, payload);
      setRunsRefreshToken((prev) => prev + 1);
    } catch (err) {
      console.error("Failed to favorite run:", err);
      updateRunFavoriteState(run, false);
      setError("Failed to save favorite run.");
    } finally {
      setRunFavoritePending(run, false);
    }
  };

  async function handleRun(
    promptOverride?: string,
    options: { useSelectedFiles?: boolean } = {},
  ) {
    // Save the prompt before clearing
    const hasPromptOverride = typeof promptOverride === "string";
    const promptText = (hasPromptOverride ? promptOverride : inputValue).trim();
    if (!promptText || isRunning || isPolling) return;
    const committingHistoricalSession = Boolean(selectedRunHistory && historicalChatSessionId);
    const historicalSessionArtifacts = artifacts;
    if (committingHistoricalSession) {
      liveChatSnapshotRef.current = null;
      setSelectedRunHistory(null);
      setSelectedRunId("");
      setHistoricalPrompt("");
      setHistoricalResult("");
      setHistoricalChatSessionId(null);
      setHistoricalThreadNotice("");
      setIsHistoryOpen(false);
      setSidePanelOpen(false);
      setLiveChatArtifacts(historicalSessionArtifacts);
      setIsHistoricalPromptExpanded(false);
      setEditingPromptId(null);
      setEditingPromptText("");
    }
    setSubmittedPrompt(promptText);
    setIsPromptExpanded(false); // Reset expand state for new prompt
    
    if (chatStreamCleanupRef.current) {
      chatStreamCleanupRef.current();
      chatStreamCleanupRef.current = null;
    }
    const localMessageStamp = Date.now();
    const localUserMessageId = `user-${localMessageStamp}`;
    const localAssistantMessageId = `assistant-${localMessageStamp}`;
    setChatTranscript((prev) => [
      ...prev,
      {
        id: localUserMessageId,
        role: "user",
        content: promptText,
        status: "completed",
      },
      {
        id: localAssistantMessageId,
        role: "assistant",
        content: "",
        status: "streaming",
        prompt: promptText,
        feedback: null,
      },
    ]);
    setRunStatus(null);
    setIsRunning(true);
    setIsPolling(false);
    setFinalResult("");
    setHasRunTriggered(true);
    setFeedbackState(null); // Reset feedback state on new run
    setIsReturningToProgress(false); // Reset flag for new run
    try {
      // Prepare file references if any files are selected
      const shouldUseSelectedFiles = options.useSelectedFiles ?? !hasPromptOverride;
      const fileReferences =
        shouldUseSelectedFiles && selectedFiles.length > 0
          ? selectedFiles.map((f) => ({
              id: f.id,
              name: f.name,
              version: f.version,
              runNumber: f.runNumber,
              thumbnail: f.thumbnail || null,
            }))
          : null;

      if (fileReferences) {
        console.log("Running agent with file references:", fileReferences);
      }

      // Capture the agent version before running
      const agentVersionAtRun = agent?.version;
      console.log("Agent version at run time:", agentVersionAtRun);

      let sessionId = chatSessionIdRef.current;
      if (!sessionId) {
        const session = await createAgentChatSession(agentId!, {
          title: promptText.slice(0, 80),
          agent_version: agentVersionAtRun,
        });
        sessionId = session.id;
        chatSessionIdRef.current = sessionId;
      }
      setChatTranscript((prev) =>
        prev.map((item) =>
          item.id === localUserMessageId || item.id === localAssistantMessageId
            ? { ...item, sessionId }
            : item,
        ),
      );

      const chatResponse = await sendAgentChatMessage(
        agentId!,
        sessionId,
        promptText,
        fileReferences as any,
      );
      const result = chatResponse.run || {};
      const eventsEndpoint = chatResponse.events_endpoint || result.events_endpoint;
      const backendAssistantMessageId = chatResponse.assistant_message?.id;
      console.log("sendAgentChatMessage result:", chatResponse);
      if (backendAssistantMessageId) {
        setChatTranscript((prev) =>
          prev.map((item) =>
            item.id === localAssistantMessageId
              ? {
                  ...item,
                  sessionId,
                  backendMessageId: backendAssistantMessageId,
                  runNumber: result.run_number ?? item.runNumber,
                }
              : item,
          ),
        );
      }
      setRunStatus(result);
      setSnackbarOpen(true);

      setIsPolling(true);
      setFinalResult("");
      setThinkingDots(0);
      dotsIntervalRef.current = setInterval(() => {
        setThinkingDots((prev) => (prev + 1) % 4);
      }, 500);

      const stripFinalAnswerHeading = (text: string): string => {
        let cleaned = text;
        cleaned = cleaned.replace(/^\s*final\s+answer:?\s*[\r\n]+/im, "");
        cleaned = cleaned.replace(/^\s*final\s+answer:?\s*/im, "");
        return cleaned.trim();
      };

      const updateAssistantTranscript = (
        text: string,
        status: ChatTranscriptItem["status"] = "streaming",
        authRequired?: AuthRequiredSignal[],
      ) => {
        setChatTranscript((prev) =>
          prev.map((item) =>
            item.id === localAssistantMessageId
              ? {
                  ...item,
                  content: text,
                  status,
                  runNumber: result.run_number ?? item.runNumber,
                  authRequired: authRequired && authRequired.length ? authRequired : item.authRequired,
                }
              : item,
          ),
        );
      };

      const stopLiveRun = () => {
        setIsPolling(false);
        OnRunCompleted();
        if (dotsIntervalRef.current) {
          clearInterval(dotsIntervalRef.current);
          dotsIntervalRef.current = null;
        }
      };

      const refreshAgent = async () => {
        try {
          const updated = await getAgent(agentId);
          setAgent(updated);
        } catch (err) {
          console.error("Failed to refresh agent data:", err);
        }
      };

      const waitForArtifacts = (ms: number) =>
        new Promise((resolve) => setTimeout(resolve, ms));

      const fetchArtifactsForRun = async (attempt = 0): Promise<any[]> => {
        try {
          const runNumber = result.run_number;
          const resolvedVersion = result.version || agentVersionAtRun;
          if (!resolvedVersion || !runNumber) return [];
          const artifactsData = await getAgentRunArtifacts(agentId!, resolvedVersion, runNumber);
          const transformedArtifacts = await Promise.all(
            (artifactsData?.artifacts || []).map(async (art: any, index: number) => {
              const normalized = normalizeRunArtifact(
                art,
                {
                  agentId: agentId!,
                  agentVersion: resolvedVersion,
                  runNumber,
                },
                index,
              );
              if (!isImageArtifact(normalized)) {
                return normalized;
              }
              const previewUrl = await fetchSecureImage(normalized.url);
              return previewUrl ? { ...normalized, previewUrl } : normalized;
            }),
          );
          if (transformedArtifacts.length === 0 && attempt < 6) {
            await waitForArtifacts(700);
            return fetchArtifactsForRun(attempt + 1);
          }
          setArtifacts((current) => mergeArtifacts(current, transformedArtifacts));
          setLiveChatArtifacts((current) => mergeArtifacts(current, transformedArtifacts));
          return transformedArtifacts;
        } catch (err) {
          console.error("Failed to fetch artifacts:", err);
          return [];
        }
      };

      const completeRun = async () => {
        setChatTranscript((prev) =>
          prev.map((item) =>
            item.id === localAssistantMessageId
              ? {
                  ...item,
                  status: item.status === "failed" ? "failed" : "completed",
                  artifactsPending: true,
                }
              : item,
          ),
        );
        stopLiveRun();
        markRunInHistory(result.run_number, "completed");
        setRunsRefreshToken((prev) => prev + 1);
        await refreshAgent();
        let finalAuthRequired: AuthRequiredSignal[] = [];
        if (result.result_endpoint) {
          try {
            const finalData = (await getAgentRunResultFromUrl(result.result_endpoint)) as any;
            finalAuthRequired = Array.isArray(finalData?.auth_required) ? finalData.auth_required : [];
          } catch (err) {
            console.error("Failed to refresh final auth metadata:", err);
          }
        }
        const runArtifacts = await fetchArtifactsForRun();
        setChatTranscript((prev) =>
          prev.map((item) =>
            item.id === localAssistantMessageId
              ? {
                  ...item,
                  artifacts: runArtifacts.length > 0
                    ? mergeArtifacts(item.artifacts || [], runArtifacts)
                    : item.artifacts,
                  artifactsPending: false,
                  authRequired: finalAuthRequired.length ? finalAuthRequired : item.authRequired,
                }
              : item,
          ),
        );
      };

      const pollResultFallback = async () => {
        if (!result.result_endpoint || !isMountedRef.current) return;
        try {
          const data = (await getAgentRunResultFromUrl(result.result_endpoint)) as any;
          if (!isMountedRef.current) return;
          const authRequired = Array.isArray(data.auth_required) ? data.auth_required : [];
          if (authRequired.length) {
            setChatTranscript((prev) =>
              prev.map((item) =>
                item.id === localAssistantMessageId
                  ? {
                      ...item,
                      runNumber: result.run_number ?? item.runNumber,
                      authRequired,
                    }
                  : item,
              ),
            );
          }
          if (data.status === "success") {
            let renderedOutput = "";
            if (typeof data.output === "string" && data.output.trim()) {
              renderedOutput = stripFinalAnswerHeading(data.output);
            } else if (data.output && typeof data.output === "object") {
              renderedOutput = JSON.stringify(data.output, null, 2);
            }
            setFinalResult(renderedOutput);
            updateAssistantTranscript(renderedOutput, "completed", authRequired);
            await completeRun();
            return;
          }
          if (data.status === "error" || data.status === "failed") {
            stopLiveRun();
            markRunInHistory(result.run_number, "failed");
            const errorText = data.message || "Agent execution failed";
            updateAssistantTranscript(errorText, "failed", authRequired);
            setRunStatus({
              status: "error",
              message: errorText,
              output: data.output,
            });
            return;
          }
        } catch (err) {
          console.error("Error polling result:", err);
        }
        pollTimeoutRef.current = setTimeout(pollResultFallback, 2000);
      };

      if (eventsEndpoint) {
        const connection = streamAgentRunResult(
          eventsEndpoint,
          (text: string) => {
            const cleaned = stripFinalAnswerHeading(text);
            setFinalResult(cleaned);
            updateAssistantTranscript(cleaned, "streaming");
          },
          (err: any) => {
            console.error("Chat stream error, falling back to result polling:", err);
            pollResultFallback();
          },
          {
            typewriterSpeed: 0,
            onAnimationEnd: completeRun,
          },
        );
        chatStreamCleanupRef.current = connection.cleanup;
      } else {
        pollResultFallback();
      }
    } catch (e) {
      setRunStatus({ status: "error", message: "Failed to run agent." });
      setChatTranscript((prev) =>
        prev.map((item) =>
          item.id === localAssistantMessageId
            ? { ...item, content: "Failed to run agent.", status: "failed" }
            : item,
        ),
      );
      setSnackbarOpen(true);
      setHasRunTriggered(false);
      OnRunCompleted();
    } finally {
    }
    if (!hasPromptOverride) {
      setInputValue("");
    }
  }

  handleRunRef.current = handleRun;

  const handlePinPrompt = async (visibility: any) => {
    const textToPin = inputValue.trim();
    if (!textToPin) {
      console.error("Cannot pin empty prompt");
      return;
    }
    try {
      const promptData = {
        prompt_text: textToPin,
        is_public: visibility?.toLowerCase() === "public",
      };

      const result = await createPinnedPromptForAgent(agentId, promptData);
      console.log("Prompt pinned successfully:", result);

      // // Refresh the pinned prompts list
      setPinnedPrompts((prev) => [...prev, result]);
    } catch (error) {
      console.error("Failed to pin prompt:", error);
    }
  };

  const getHelpText = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 0.5, // Space between hint and your textfield
          px: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.72rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="span"
            sx={{
              color: "#b87333",
              bgcolor: "rgba(184, 115, 51, 0.1)",
              px: 0.6,
              py: 0.1,
              borderRadius: 1,
              fontFamily: "monospace",
              fontWeight: "bold",
              mr: 0.5,
            }}
          >
            @file
          </Box>
          adds files
        </Typography>
      </Box>
    );
  };

  const renderAssetUploadNotice = () => {
    if (!assetUploadNotice && !assetUploadStatusLabel && !assetUploadError) return null;
    return (
      <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
        <Chip
          size="small"
          icon={
            assetUploadBusy ? (
              <CircularProgress size={13} sx={{ color: `${COPPER_DARK} !important` }} />
            ) : (
              <FolderOpenIcon sx={{ fontSize: "15px !important" }} />
            )
          }
          label={
            assetUploadError
              ? assetUploadError
              : assetUploadBusy
              ? assetUploadStatusLabel
              : `${assetUploadNotice?.fileCount || 0} asset${assetUploadNotice?.fileCount === 1 ? "" : "s"} queued in ${assetUploadNotice?.indexName}`
          }
          onDelete={!assetUploadBusy ? () => {
            setAssetUploadNotice(null);
            setAssetUploadError("");
          } : undefined}
          sx={{
            ...copperChipSx,
            bgcolor: assetUploadError ? "rgba(220, 38, 38, 0.06)" : "rgba(184, 115, 51, 0.06)",
            borderColor: assetUploadError ? "rgba(220, 38, 38, 0.28)" : COPPER_BORDER,
            color: assetUploadError ? "#991B1B" : copperChipSx.color,
            maxWidth: "100%",
            "& .MuiChip-label": {
              fontSize: "0.76rem",
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        />
      </Box>
    );
  };

  const handleClearPrompt = () => {
    setInputValue("");
    setAttachedFiles([]);
  };

  const handleCopyResult = () => {
    navigator.clipboard.writeText(finalResult);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  const handleCopyHistoricalPrompt = () => {
    navigator.clipboard.writeText(historicalPrompt);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 2000);
  };

  const handleThumbsUp = () => {
    setFeedbackState(feedbackState === "up" ? null : "up");
  };

  const handleThumbsDown = () => {
    setFeedbackState(feedbackState === "down" ? null : "down");
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEnhancePrompt = async () => {
    if (!inputValue.trim() || !agent) return;
    setIsEnhancing(true);
    setIsModalOpen(true);

    try {
      const result = await enhancePrompt({
        role: agent.role,
        description: agent.description,
        input_prompt: inputValue,
        tools: agent.tools,
        mode: "zero_shot",
        model: "gpt-4o",
        llm_overrides: null,
      });
      setEnhancedSuggestion(result.enhanced_prompt);
    } catch (err) {
      console.error("Failed to enhance prompt:", err);
      setEnhancedSuggestion("Failed to generate enhancement");
    } finally {
      setIsEnhancing(false);
    }
  };

  const handleDeletePinnedPrompt = async (promptId: string) => {
    try {
      await deletePinnedPromptForAgent(agentId!, promptId);
      setPinnedPrompts((prev) => prev.filter((p) => p.id !== promptId));
    } catch (error) {
      console.error("Failed to delete pinned prompt:", error);
    }
  };

  const handlePinnedPromptClick = (inputValue: string) => {
    setInputValue(inputValue);
  };

  const handleBackToLiveChat = () => {
    setSelectedRunHistory(null);
    setSelectedRunId("");
    setHistoricalPrompt("");
    setHistoricalResult("");
    setHistoricalChatSessionId(null);
    setHistoricalThreadNotice("");
    setIsHistoricalPromptExpanded(false);
    setEditingPromptId(null);
    setEditingPromptText("");

    if (restoreLiveChatSnapshot()) {
      return;
    }

    // If the page was opened directly into a historical run, there is no prior
    // live-session snapshot to restore.
    setChatTranscript([]);
    chatSessionIdRef.current = null;

    // If there's an active run in progress, restore it. Otherwise this was a
    // direct historical view without a prior live-session snapshot.
    if (isPolling || isRunning) {
      setIsReturningToProgress(true);
      setArtifacts(liveChatArtifacts);
    } else {
      setHasRunTriggered(false);
      setFinalResult("");
      setIsPolling(false);
      setRunStatus(null);
      setArtifacts([]);
      setInputValue("");
      setSubmittedPrompt(""); // Clear the submitted prompt bubble
      setIsPromptExpanded(false); // Reset expand state
      setSelectedFiles([]);
      setIsReturningToProgress(false);
    }
  };

  const handleCloseRunHistory = () => {
    setIsHistoryOpen(false);
    if (selectedRunHistory) {
      handleBackToLiveChat();
    }
  };

  const agentDisplayName = agent?.display_name || "Agent Run";
  const agentShortName =
    (agent?.display_name || "this agent")
      .replace(/\s+\(Copy\)$/i, "")
      .split(" - ")[0]
      .trim() || "this agent";
  const composerPlaceholder = `Ask ${agentShortName}...`;

  const getUploadRelativePath = (file: UploadableFile) =>
    file.relativePath || file.webkitRelativePath || file.name;

  const ensureChatSessionForUpload = async () => {
    if (chatSessionIdRef.current) return chatSessionIdRef.current;
    if (!agentId) throw new Error("Agent is not loaded yet.");
    const session = await createAgentChatSession(agentId, {
      title: "Uploaded files",
      agent_version: agent?.version,
    });
    chatSessionIdRef.current = session.id;
    return session.id as string;
  };

  const dedupeUploadFiles = (files: UploadableFile[]) => {
    const seen = new Set<string>();
    return files.filter((file) => {
      const key = `${getUploadRelativePath(file)}:${file.size}:${file.lastModified}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const uploadAssetsToChat = async (files: UploadableFile[]) => {
    if (!agentId || files.length === 0) return;
    const uploadFiles = dedupeUploadFiles(files);
    if (uploadFiles.length === 0) return;

    setAssetUploadError("");
    setAssetUploadStatus("uploading");
    try {
      const formData = new FormData();
      uploadFiles.forEach((file) => {
        formData.append("files", file, file.name);
        formData.append("paths", getUploadRelativePath(file));
      });

      const sessionId = await ensureChatSessionForUpload();
      const uploadResult = await uploadAgentChatSessionFiles(agentId, sessionId, formData);
      setAssetUploadStatus("indexing");
      setAssetUploadNotice({
        indexName: "this chat",
        fileCount: uploadResult?.file_count || uploadFiles.length,
      });
      setAssetUploadStatus("success");
      setInputValue((current) =>
        current.trim()
          ? current
          : "Use the files I uploaded in this chat to answer my next question.",
      );
    } catch (err: any) {
      console.error("Failed to upload/index assets:", err);
      setAssetUploadError(err?.message || "Failed to upload and index assets.");
      setAssetUploadStatus("error");
    }
  };

  const handleAssetInputFiles = (fileList: FileList | null) => {
    const files = Array.from(fileList || []) as UploadableFile[];
    if (files.length > 0) {
      void uploadAssetsToChat(files);
    }
  };

  const artifactToUploadFile = async (artifact: any): Promise<UploadableFile | null> => {
    const filename = String(artifact?.filename || artifact?.name || "artifact").trim();
    if (!filename || !artifact?.agent_id || !artifact?.agent_version || !artifact?.run_number) {
      return null;
    }
    const response = await downloadArtifactFile(
      artifact.agent_id,
      artifact.agent_version,
      artifact.run_number,
      filename,
    );
    if (!response || !response.ok) {
      throw new Error(`Could not fetch artifact ${filename}`);
    }
    const blob = await response.blob();
    return new File([blob], filename, {
      type: blob.type || "application/octet-stream",
      lastModified: Date.now(),
    }) as UploadableFile;
  };

  const readDraggedArtifact = (event: React.DragEvent): any | null => {
    const types = Array.from(event.dataTransfer.types || []);
    if (!types.includes("application/x-nextmoca-artifact")) return null;
    try {
      return JSON.parse(event.dataTransfer.getData("application/x-nextmoca-artifact"));
    } catch {
      return null;
    }
  };

  const readDroppedEntryFiles = async (
    entry: any,
    parentPath = "",
  ): Promise<UploadableFile[]> => {
    const entryPath = parentPath ? `${parentPath}/${entry.name}` : entry.name;

    if (entry.isFile) {
      return new Promise((resolve, reject) => {
        entry.file(
          (file: File) => {
            resolve([Object.assign(file, { relativePath: entryPath }) as UploadableFile]);
          },
          reject,
        );
      });
    }

    if (entry.isDirectory && entry.createReader) {
      const reader = entry.createReader();
      const entries: any[] = [];
      await new Promise<void>((resolve, reject) => {
        const readBatch = () => {
          reader.readEntries(
            (batch: any[]) => {
              if (!batch.length) {
                resolve();
                return;
              }
              entries.push(...batch);
              readBatch();
            },
            reject,
          );
        };
        readBatch();
      });
      const nested = await Promise.all(
        entries.map((childEntry) => readDroppedEntryFiles(childEntry, entryPath)),
      );
      return nested.flat();
    }

    return [];
  };

  const readDroppedUploadFiles = async (dataTransfer: DataTransfer): Promise<UploadableFile[]> => {
    const items = Array.from(dataTransfer.items || []);
    const entries = items
      .filter((item) => item.kind === "file")
      .map((item) => (item as any).webkitGetAsEntry?.())
      .filter(Boolean);

    if (entries.length > 0) {
      const nested = await Promise.all(entries.map((entry) => readDroppedEntryFiles(entry)));
      return nested.flat();
    }

    return Array.from(dataTransfer.files || []) as UploadableFile[];
  };

  const handleAssetDragOver = (event: React.DragEvent) => {
    const hasFiles = Array.from(event.dataTransfer.types || []).includes("Files");
    const hasArtifact = Array.from(event.dataTransfer.types || []).includes("application/x-nextmoca-artifact");
    if (!hasFiles && !hasArtifact) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setAssetDropActive(true);
  };

  const handleAssetDrop = async (event: React.DragEvent) => {
    const hasFileSignal = Array.from(event.dataTransfer.types || []).includes("Files");
    const draggedArtifact = readDraggedArtifact(event);
    if (!hasFileSignal && !draggedArtifact) return;
    event.preventDefault();
    setAssetDropActive(false);
    try {
      if (hasFileSignal) {
        const droppedFiles = await readDroppedUploadFiles(event.dataTransfer);
        if (droppedFiles.length > 0) {
          await uploadAssetsToChat(droppedFiles);
        }
        return;
      }
      const artifactFile = await artifactToUploadFile(draggedArtifact);
      if (artifactFile) {
        await uploadAssetsToChat([artifactFile]);
      }
    } catch (err: any) {
      setAssetUploadError(err?.message || "Could not prepare dragged asset.");
    }
  };

  const moveHistoricalPromptToComposer = () => {
    const nextText = historicalPrompt.trim();
    if (!nextText || isRunning || isPolling) return;
    setSelectedRunHistory(null);
    setHistoricalPrompt("");
    setHistoricalResult("");
    setHasRunTriggered(true);
    setArtifacts(liveChatArtifacts);
    setInputValue(nextText);
    setTimeout(() => {
      const textarea = inputRef.current?.querySelector("textarea") as HTMLTextAreaElement | null;
      textarea?.focus();
    }, 0);
  };

  const rerunHistoricalPrompt = async () => {
    const nextText = historicalPrompt.trim();
    if (!nextText || isRunning || isPolling) return;
    setSelectedRunHistory(null);
    setHistoricalPrompt("");
    setHistoricalResult("");
    setHasRunTriggered(true);
    setArtifacts(liveChatArtifacts);
    await rerunPrompt(nextText);
  };

  const selectedRunTitle = selectedRunHistory
    ? String(
        selectedRunHistory.run_label ||
          parsePrompt(selectedRunHistory.agent_prompt || "") ||
          `Attempt ${selectedRunHistory.run_number}`,
      ).trim()
    : "";
  const selectedRunKey = selectedRunHistory ? getRunKey(selectedRunHistory) : null;
  const hasArtifacts = artifacts.length > 0;
  const hasPinnedPrompts = pinnedPrompts.length > 0;
  const assetUploadBusy = ["uploading", "indexing", "attaching"].includes(assetUploadStatus);
  const assetUploadStatusLabel =
    assetUploadStatus === "uploading"
      ? "Uploading assets..."
      : assetUploadStatus === "indexing"
        ? "Indexing files for this chat..."
        : assetUploadStatus === "attaching"
          ? "Attaching to agent..."
          : "";
  const isLeftDrawerOpen = isHistoryOpen || sidePanelOpen;
  const leftDrawerWidth = sidePanelOpen
    ? { xs: "88vw", sm: 390, md: 430 }
    : { xs: "86vw", sm: 330, md: 330 };
  const shouldAllowTranscriptScroll = Boolean(
    selectedRunHistory || hasRunTriggered || isPolling || finalResult,
  );
  const shouldAnchorLiveTranscriptToComposer = Boolean(
    !selectedRunHistory && hasRunTriggered,
  );
  const pageSurface = isDark ? "#0F0D0B" : SURFACE;
  const contentSurface = isDark ? "#181411" : SURFACE;
  const elevatedSurface = isDark ? "#211B16" : "#FFFFFF";
  const overlaySurface = isDark ? "rgba(15,13,11,0.88)" : "rgba(255, 253, 252, 0.9)";
  const textPrimary = isDark ? "text.primary" : TEXT_PRIMARY;
  const textMuted = isDark ? "text.secondary" : TEXT_MUTED;
  const copperText = isDark ? "#F2C69B" : COPPER_DARK;
  const copperBorder = isDark ? "rgba(224,177,132,0.28)" : COPPER_BORDER;
  const dividerColor = isDark ? "rgba(224,177,132,0.18)" : "rgba(226, 232, 240, 0.7)";
  const drawerBorder = isDark ? "rgba(224,177,132,0.22)" : "rgba(226, 232, 240, 0.85)";
  const scrollThumbColor = isDark ? "rgba(224,177,132,0.28)" : "rgba(0,0,0,0.1)";
  const activeActionSx = {
    color: copperText,
    borderColor: isDark ? "rgba(224,177,132,0.46)" : COPPER,
    bgcolor: elevatedSurface,
  };
  const themedUserBubbleSx = {
    ...userBubbleSx,
    bgcolor: isDark ? "#211B16" : SURFACE,
    border: `1px solid ${copperBorder}`,
    color: textPrimary,
    boxShadow: isDark ? "0 14px 34px rgba(0,0,0,0.34)" : userBubbleSx.boxShadow,
  };
  const themedAssistantBubbleSx = {
    ...assistantBubbleSx,
    bgcolor: isDark ? "#181411" : SURFACE,
    border: `1px solid ${isDark ? "rgba(224,177,132,0.24)" : "rgba(184, 115, 51, 0.35)"}`,
    color: textPrimary,
    boxShadow: isDark ? "0 14px 34px rgba(0,0,0,0.32)" : assistantBubbleSx.boxShadow,
    "& pre": {
      ...assistantBubbleSx["& pre"],
      bgcolor: isDark ? "rgba(255,247,239,0.08)" : "rgba(15,23,42,0.05)",
    },
    "& code": {
      bgcolor: isDark ? "rgba(255,247,239,0.08)" : "rgba(184, 115, 51, 0.09)",
      color: isDark ? "#FFF7EF" : COPPER_DARK,
    },
  };
  const themedComposerSx = {
    ...composerSx,
    "& .MuiOutlinedInput-root": {
      ...composerSx["& .MuiOutlinedInput-root"],
      bgcolor: isDark ? "#211B16" : "#FFFFFF",
      color: textPrimary,
      boxShadow: isDark ? "0 18px 48px rgba(0,0,0,0.34)" : "0 18px 48px rgba(17, 24, 39, 0.08)",
      "& fieldset": {
        borderColor: copperBorder,
        borderWidth: "1.5px",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "rgba(224,177,132,0.46)" : COPPER,
      },
      "&.Mui-focused fieldset": {
        borderColor: isDark ? "#D1935C" : COPPER,
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textMuted,
      opacity: 1,
    },
  };

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        bgcolor: pageSurface,
        color: "text.primary",
        px: PAGE_SHELL.paddingX,
        py: PAGE_SHELL.paddingY,
        maxWidth: PAGE_SHELL.maxWidth,
        mx: "auto",
        width: "100%",
        overflow: "hidden",
      }}
    >
      <input
        ref={assetFileInputRef}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          handleAssetInputFiles(event.target.files);
          event.currentTarget.value = "";
        }}
      />
      <input
        ref={assetFolderInputRef}
        type="file"
        multiple
        hidden
        // @ts-ignore - webkitdirectory is not in the TypeScript DOM types
        webkitdirectory="true"
        directory="true"
        onChange={(event) => {
          handleAssetInputFiles(event.target.files);
          event.currentTarget.value = "";
        }}
      />
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 0,
          pb: PAGE_SHELL.headerMarginBottom,
          px: 0,
          borderBottom: `1px solid ${dividerColor}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              ...PAGE_TYPOGRAPHY.heading,
              color: textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {agentDisplayName}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Tooltip title="Upload files">
            <IconButton
              size="small"
              onClick={() => assetFileInputRef.current?.click()}
              sx={messageActionButtonSx(isDark, assetUploadBusy)}
              disabled={assetUploadBusy}
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Upload folder">
            <IconButton
              size="small"
              onClick={() => assetFolderInputRef.current?.click()}
              sx={messageActionButtonSx(isDark, assetUploadBusy)}
              disabled={assetUploadBusy}
            >
              <DriveFolderUploadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Run history">
            <IconButton
              size="small"
              onClick={() => {
                setSidePanelOpen(false);
                if (isHistoryOpen) {
                  handleCloseRunHistory();
                } else {
                  setIsHistoryOpen(true);
                }
              }}
              sx={{
                ...messageActionButtonSx(isDark, false),
                ...(isHistoryOpen ? activeActionSx : {}),
              }}
            >
              <Badge
                badgeContent={totalHistoryRuns || agentRuns.length}
                color="primary"
                max={99}
                sx={{
                  "& .MuiBadge-badge": {
                    bgcolor: COPPER,
                    color: "#fff",
                    fontSize: "0.62rem",
                    minWidth: 16,
                    height: 16,
                  },
                }}
              >
                <HistoryIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title={hasArtifacts ? "Artifacts" : "No artifacts yet"}>
            <span>
              <IconButton
                size="small"
                disabled={!hasArtifacts}
                onClick={() => {
                  if (!hasArtifacts) return;
                  setIsHistoryOpen(false);
                  setSidePanelOpen((open) => (open && sidePanelTab === 0 ? false : true));
                  setSidePanelTab(0);
                }}
                sx={{
                  ...messageActionButtonSx(isDark, !hasArtifacts),
                  ...(sidePanelOpen && sidePanelTab === 0 ? activeActionSx : {}),
                }}
              >
                <Badge
                  badgeContent={artifacts.length}
                  color="primary"
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: COPPER,
                      color: "#fff",
                      fontSize: "0.62rem",
                      minWidth: 16,
                      height: 16,
                    },
                  }}
                >
                  <InsertDriveFileIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={hasPinnedPrompts ? "Pinned prompts" : "No pinned prompts yet"}>
            <span>
              <IconButton
                size="small"
                disabled={!hasPinnedPrompts}
                onClick={() => {
                  if (!hasPinnedPrompts) return;
                  if (isHistoryOpen) {
                    handleCloseRunHistory();
                  } else {
                    setIsHistoryOpen(false);
                  }
                  setSidePanelOpen((open) => (open && sidePanelTab === 1 ? false : true));
                  setSidePanelTab(1);
                }}
                sx={{
                  ...messageActionButtonSx(isDark, !hasPinnedPrompts),
                  ...(sidePanelOpen && sidePanelTab === 1 ? activeActionSx : {}),
                }}
              >
                <Badge
                  badgeContent={pinnedPrompts.length}
                  color="primary"
                  max={99}
                  sx={{
                    "& .MuiBadge-badge": {
                      bgcolor: COPPER,
                      color: "#fff",
                      fontSize: "0.62rem",
                      minWidth: 16,
                      height: 16,
                    },
                  }}
                >
                  <PushPinIcon />
                </Badge>
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
      {/* Container for Sidebars + Content */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: isLeftDrawerOpen ? leftDrawerWidth : 0,
            minWidth: isLeftDrawerOpen ? leftDrawerWidth : 0,
            maxWidth: isLeftDrawerOpen ? leftDrawerWidth : 0,
            overflow: "hidden",
            transition: "width 260ms cubic-bezier(0.2, 0, 0, 1), min-width 260ms cubic-bezier(0.2, 0, 0, 1), max-width 260ms cubic-bezier(0.2, 0, 0, 1)",
            borderRight: isLeftDrawerOpen ? `1px solid ${drawerBorder}` : "1px solid transparent",
            bgcolor: contentSurface,
            flexShrink: 0,
          }}
        >
          {isHistoryOpen ? (
            <HistorySidebar
              isOpen={isHistoryOpen}
              fullWidth
              agentRuns={agentRuns}
              selectedRunNumber={selectedRunHistory?.run_number || null}
              selectedRunKey={selectedRunKey}
              hasMoreHistory={hasMoreHistory}
              isLoadingMoreHistory={isLoadingMoreHistory}
              totalHistoryRuns={totalHistoryRuns}
              onToggleClose={handleCloseRunHistory}
              onSelectRun={(run) => {
                handleSelectHistoricalRun(run);
              }}
              onLoadMore={handleLoadMoreHistory}
              onRefresh={() => {
                setHistoryPage(1);
                setAgentRuns([]);
                setRunsRefreshToken((token) => token + 1);
              }}
              onRenameRun={async (run, newLabel) => {
                await renameAgentRun(run.agent_id, run.agent_version, run.run_number, newLabel);
                const targetRunKey = getRunKey(run);
                setAgentRuns((prev) =>
                  prev.map((item) =>
                    getRunKey(item) === targetRunKey ? { ...item, run_label: newLabel || null } : item,
                  ),
                );
                setSelectedRunHistory((current: any) =>
                  current && getRunKey(current) === targetRunKey
                    ? { ...current, run_label: newLabel || null }
                    : current,
                );
              }}
              onFavoriteRun={favoriteRun}
              favoriteRunPendingKeys={favoriteRunPendingKeys}
            />
          ) : sidePanelOpen ? (
            <ArtifactsAndPinnedPromptsSideBar
              prompts={pinnedPrompts}
              artifacts={artifacts}
              onCopy={handleCopyPrompt}
              onDelete={handleDeletePrompt}
              onArtifactClick={handleArtifactDownload}
              deleting={deleting}
              error={error}
              initialTab={sidePanelTab}
              onClose={() => setSidePanelOpen(false)}
            />
          ) : null}
        </Box>
        {/* MAIN CONTENT AREA */}
        <Box
          onDragOver={handleAssetDragOver}
          onDragLeave={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setAssetDropActive(false);
            }
          }}
          onDrop={handleAssetDrop}
          sx={{
            display: "flex",
            flexGrow: 1,
            minHeight: 0,
            flexDirection: "row",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {assetDropActive && (
            <Box
              sx={{
                position: "absolute",
                inset: 14,
                zIndex: 20,
                borderRadius: "24px",
                border: `1.5px dashed ${COPPER}`,
                bgcolor: overlaySurface,
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Stack alignItems="center" spacing={1.2}>
                <CloudUploadIcon sx={{ fontSize: 42, color: COPPER }} />
                <Typography variant="h6" sx={{ color: copperText, fontWeight: 800 }}>
                  Drop assets to index
                </Typography>
                <Typography variant="body2" sx={{ color: textMuted }}>
                  Files, folders, and generated artifacts can become agent knowledge.
                </Typography>
              </Stack>
            </Box>
          )}
          {/* MAIN CONTENT - Middle section : Prompt + Response */}
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              boxSizing: "border-box",
              position: "relative",
              overflow: "hidden", // Changed from "auto" to "hidden" - children will handle scrolling
              bgcolor: contentSurface,
            }}
          >
            {/* Scrollable Content Area */}
            <Box
              ref={chatScrollRef}
              onScroll={updateNewOutputIndicator}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflowY: shouldAllowTranscriptScroll ? "auto" : "hidden",
                overflowX: "hidden",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-track": { background: "transparent" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: scrollThumbColor,
                  borderRadius: "10px",
                  "&:hover": { backgroundColor: isDark ? "rgba(224,177,132,0.42)" : "#b06d50" },
                },
                px: { xs: 2, md: 3 },
                pt: { xs: 2, md: 3 },
                pb: 2,
                bgcolor: contentSurface,
              }}
            >
              {/* Run History View */}
              {selectedRunHistory ? (
	                <Box
	                  sx={{
	                    flexGrow: 1,
	                    width: "100%",
	                    maxWidth: "920px",
	                    mx: "auto",
	                    overflow: "visible",
	                    pb: 6,
	                  }}
	                >
	                  <Box
	                    sx={{
	                      mb: 3,
	                      display: "flex",
	                      justifyContent: "space-between",
	                      alignItems: "center",
	                      gap: 1.5,
	                      px: 0.5,
	                    }}
	                  >
	                    <Box sx={{ minWidth: 0 }}>
	                      <Typography
	                        variant="subtitle2"
	                        sx={{
	                          color: textPrimary,
	                          fontWeight: 800,
	                          lineHeight: 1.2,
	                          overflow: "hidden",
	                          textOverflow: "ellipsis",
	                          whiteSpace: "nowrap",
	                        }}
	                      >
	                        {selectedRunTitle}
	                      </Typography>
	                      <Typography variant="caption" sx={{ color: textMuted }}>
	                        Saved run · Version {selectedRunHistory.agent_version}
	                        {selectedRunHistory.created_at ? ` · ${new Date(selectedRunHistory.created_at).toLocaleString()}` : ""}
	                      </Typography>
	                    </Box>
	                  </Box>

                  {isLoadingHistorical ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "200px",
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Stack spacing={2.25}>
                      {historicalThreadNotice && (
                        <Chip
                          size="small"
                          label={historicalThreadNotice}
                          sx={{
                            alignSelf: "flex-start",
                            bgcolor: historicalChatSessionId ? "rgba(184, 115, 51, 0.08)" : "rgba(100, 116, 139, 0.08)",
                            color: historicalChatSessionId ? COPPER_DARK : TEXT_MUTED,
                            border: `1px solid ${historicalChatSessionId ? COPPER_BORDER : "rgba(148, 163, 184, 0.28)"}`,
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {historicalChatSessionId && chatTranscript.length > 0 ? (
                        <Stack spacing={2.2}>
                          {chatTranscript.map((message) => {
                            const isUser = message.role === "user";
                            const isFailed = message.status === "failed";
                            const hasInlineImages =
                              !isUser && (message.artifacts || []).some(isImageArtifact);
                            const isWaitingForArtifacts = !isUser && !!message.artifactsPending;
                            if (
                              !isUser &&
                              !message.content.trim() &&
                              !isFailed &&
                              !hasInlineImages &&
                              !isWaitingForArtifacts
                            ) {
                              if (message.status === "streaming") return null;
                            }
                            return (
                              <Box
                                key={message.id}
                                sx={{
                                  display: "flex",
                                  justifyContent: isUser ? "flex-end" : "flex-start",
                                }}
                              >
                                <Box sx={{ maxWidth: { xs: "94%", md: isUser ? "76%" : "82%" }, width: isUser ? "fit-content" : "100%" }}>
                                  <Box
                                    sx={{
                                      ...(isUser ? themedUserBubbleSx : themedAssistantBubbleSx),
                                      ...(isFailed
                                        ? {
                                            bgcolor: "#FEF2F2",
                                            color: "#991B1B",
                                            borderColor: "rgba(185, 28, 28, 0.28)",
                                          }
                                        : {}),
                                    }}
                                  >
                                    {isUser ? (
                                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                                        {message.content}
                                      </Typography>
                                    ) : (
                                      <>
                                        {message.content.trim() ? (
                                          <ReactMarkdown components={markdownComponents}>
                                            {sanitizeArtifactReferences(message.content)}
                                          </ReactMarkdown>
                                        ) : isWaitingForArtifacts ? null : hasInlineImages ? null : (
                                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                            Agent completed without a text response.
                                          </Typography>
                                        )}
                                        {isWaitingForArtifacts && !hasInlineImages ? <ArtifactPendingIndicator /> : null}
                                        <InlineImageArtifacts artifacts={message.artifacts} />
                                        <AuthRequiredConnectCard message={message} />
                                        <OutcomeFeedbackToolbar message={message} />
                                      </>
                                    )}
                                  </Box>
                                  <Stack
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent={isUser ? "flex-end" : "flex-start"}
                                    sx={{ mt: 0.65, px: 0.25 }}
                                  >
                                    <Tooltip title={isUser ? "Copy prompt" : "Copy response"}>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyTextToClipboard(message.content)}
                                        sx={messageActionButtonSx(isDark, false)}
                                      >
                                        <CopyIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title={isUser ? "Share prompt" : "Share response"}>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          openShareDialog({
                                            kind: isUser ? "prompt" : "response",
                                            title: selectedRunTitle || agentDisplayName,
                                            text: message.content || (isUser ? "Prompt" : "Agent response"),
                                            runNumber: message.runNumber,
                                            agentVersion: selectedRunHistory?.agent_version,
                                            messageId: message.id,
                                          })
                                        }
                                        sx={messageActionButtonSx(isDark, false)}
                                      >
                                        <ShareIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <>
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <Box sx={{ maxWidth: { xs: "94%", md: "76%" }, width: "fit-content" }}>
                          <Box
                            sx={{
                              ...themedUserBubbleSx,
                              cursor: historicalPrompt.length > 240 ? "pointer" : "default",
                            }}
                            onClick={() => {
                              if (historicalPrompt.length > 240) {
                                setIsHistoricalPromptExpanded((prev) => !prev);
                              }
                            }}
                          >
                            <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                              {isHistoricalPromptExpanded || historicalPrompt.length <= 240
                                ? historicalPrompt
                                : `${historicalPrompt.substring(0, 240)}...`}
                            </Typography>
                          </Box>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            sx={{ mt: 0.65, pr: 0.25 }}
                          >
                            <Tooltip title="Copy prompt">
                              <IconButton
                                size="small"
                                onClick={handleCopyHistoricalPrompt}
                                sx={messageActionButtonSx(isDark, false)}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Share prompt">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openShareDialog({
                                    kind: "prompt",
                                    title: selectedRunTitle || agentDisplayName,
                                    text: historicalPrompt,
                                    runNumber: selectedRunHistory?.run_number,
                                    agentVersion: selectedRunHistory?.agent_version,
                                  })
                                }
                                sx={messageActionButtonSx(isDark, false)}
                              >
                                <ShareIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit in composer">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isRunning || isPolling}
                                  onClick={moveHistoricalPromptToComposer}
                                  sx={messageActionButtonSx(isDark, isRunning || isPolling)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Rerun prompt">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={isRunning || isPolling}
                                  onClick={rerunHistoricalPrompt}
                                  sx={messageActionButtonSx(isDark, isRunning || isPolling)}
                                >
                                  <RunIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {historicalPrompt.length > 240 && (
                              <Tooltip title={isHistoricalPromptExpanded ? "Collapse prompt" : "Expand prompt"}>
                                <IconButton
                                  size="small"
                                  onClick={() => setIsHistoricalPromptExpanded((prev) => !prev)}
                                  sx={messageActionButtonSx(isDark, false)}
                                >
                                  <ExpandMoreIcon
                                    sx={{
                                      transition: "transform 0.18s ease",
                                      transform: isHistoricalPromptExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                        <Box sx={{ maxWidth: { xs: "96%", md: "82%" }, width: "100%" }}>
                          <Box sx={themedAssistantBubbleSx}>
                            <ReactMarkdown components={markdownComponents}>
                              {sanitizeArtifactReferences(historicalResult)}
                            </ReactMarkdown>
                            <InlineImageArtifacts artifacts={artifacts} />
                          </Box>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.8, ml: 0.25 }}>
                            <Tooltip title="Share response">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  openShareDialog({
                                    kind: "response",
                                    title: selectedRunTitle || agentDisplayName,
                                    text: historicalResult || "Agent response",
                                    runNumber: selectedRunHistory?.run_number,
                                    agentVersion: selectedRunHistory?.agent_version,
                                  })
                                }
                                sx={messageActionButtonSx(isDark, false)}
                              >
                                <ShareIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy response">
                              <IconButton
                                size="small"
                                onClick={() => copyTextToClipboard(historicalResult)}
                                sx={messageActionButtonSx(isDark, false)}
                              >
                                <CopyIcon />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>
                      </Box>
                        </>
                      )}
                    </Stack>
                  )}
                </Box>
              ) : (
                <>
                  {/* LIVE CHAT VIEW */}
                  {/* Wrapper for centered layout */}
                  <Box
                    sx={{
                      maxWidth: "900px", // Centered max width for chat
                      mx: "auto", // Center the content
                      width: "100%",
                      ...(shouldAllowTranscriptScroll
                        ? {
                            mt: shouldAnchorLiveTranscriptToComposer ? "auto" : 0,
                          }
                        : {
                            position: "absolute",
                            inset: 0,
                            maxWidth: "none",
                            mx: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            boxSizing: "border-box",
                            px: { xs: 1, md: 4 },
                          }),
                    }}
                  >
                    {/* Contextual prompt bubble shows after user input */}
                    {chatTranscript.length === 0 && (isPolling || isRunning || finalResult) && (
                      <Box
                        sx={{
                          mb: 3,
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            ...themedUserBubbleSx,
                            maxWidth: { xs: "94%", md: "76%" },
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: submittedPrompt.length > 90 ? "pointer" : "default",
                          }}
                          onClick={() => {
                            if (submittedPrompt.length > 90) {
                              setIsPromptExpanded(!isPromptExpanded);
                            }
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: textPrimary,
                              flexGrow: 1,
                              wordBreak: "break-word",
                              lineHeight: 1.7,
                            }}
                          >
                            {isPromptExpanded || submittedPrompt.length <= 90
                              ? submittedPrompt
                              : `${submittedPrompt.substring(0, 90)}...`}
                          </Typography>
                          {submittedPrompt.length > 90 && (
                            <IconButton
                              size="small"
                              sx={{
                                padding: 0.5,
                                color: copperText,
                                transition: "transform 0.2s",
                                transform: isPromptExpanded ? "rotate(180deg)" : "rotate(0deg)",
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )}

                    {chatTranscript.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                        {chatTranscript.map((message) => {
                          const isUser = message.role === "user";
                          const isFailed = message.status === "failed";
                          const isEditingPrompt = editingPromptId === message.id;
                          const hasInlineImages =
                            !isUser && (message.artifacts || []).some(isImageArtifact);
                          const isWaitingForArtifacts = !isUser && !!message.artifactsPending;
                          const promptActionsDisabled = isRunning || isPolling;
                          if (
                            !isUser &&
                            !message.content.trim() &&
                            !isFailed &&
                            !hasInlineImages &&
                            !isWaitingForArtifacts
                          ) {
                            if (message.status === "streaming") return null;
                          }
                          return (
                            <Box
                              key={message.id}
                              sx={{
                                display: "flex",
                                justifyContent: isUser ? "flex-end" : "flex-start",
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: { xs: "92%", md: "76%" },
                                }}
                              >
                                <Box
                                  sx={{
                                    ...(isUser ? themedUserBubbleSx : themedAssistantBubbleSx),
                                    ...(isFailed
                                      ? {
                                          bgcolor: "#FEF2F2",
                                          color: "#991B1B",
                                          borderColor: "rgba(185, 28, 28, 0.28)",
                                        }
                                      : {}),
                                  }}
                                >
                                {isUser ? (
                                  <Box
                                    sx={{
                                      position: "static",
                                    }}
                                  >
                                    {isEditingPrompt ? (
                                      <Stack spacing={1}>
                                        <TextField
                                          value={editingPromptText}
                                          onChange={(event) => setEditingPromptText(event.target.value)}
                                          multiline
                                          minRows={2}
                                          maxRows={8}
                                          autoFocus
                                          fullWidth
                                          variant="outlined"
                                          size="small"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              bgcolor: "rgba(255,255,255,0.72)",
                                              borderRadius: 2,
                                              color: "#111827",
                                            },
                                          }}
                                        />
                                        <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                                          <Tooltip title="Cancel edit">
                                            <span>
                                              <IconButton size="small" onClick={cancelPromptEdit} sx={messageActionButtonSx(isDark, false)}>
                                                <CloseIcon />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Save edit">
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={savePromptEdit}
                                                disabled={!editingPromptText.trim()}
                                                sx={messageActionButtonSx(isDark, !editingPromptText.trim())}
                                              >
                                                <CheckIcon />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Save and rerun">
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={rerunEditedPrompt}
                                                disabled={!editingPromptText.trim() || promptActionsDisabled}
                                                sx={messageActionButtonSx(isDark, !editingPromptText.trim() || promptActionsDisabled)}
                                              >
                                                <RunIcon />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </Stack>
                                      </Stack>
                                    ) : (
                                      <>
                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}>
                                          {message.content}
                                        </Typography>
                                      </>
                                    )}
                                  </Box>
                                ) : (
                                  <>
                                    {message.content.trim() ? (
                                      <ReactMarkdown components={markdownComponents}>
                                        {sanitizeArtifactReferences(message.content)}
                                      </ReactMarkdown>
                                    ) : isWaitingForArtifacts ? null : hasInlineImages ? null : (
                                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                        Agent completed without a text response.
                                      </Typography>
                                    )}
                                    {isWaitingForArtifacts && !hasInlineImages ? (
                                      <ArtifactPendingIndicator />
                                    ) : null}
                                    <InlineImageArtifacts artifacts={message.artifacts} />
                                    <AuthRequiredConnectCard message={message} />
                                    <OutcomeFeedbackToolbar message={message} />
                                  </>
                                )}
                                </Box>
                                {isUser && !isEditingPrompt && (
                                  <Stack
                                    className="prompt-actions"
                                    direction="row"
                                    spacing={0.5}
                                    justifyContent="flex-end"
                                    sx={{ mt: 0.65, pr: 0.25 }}
                                  >
                                    <Tooltip title="Copy prompt">
                                      <IconButton
                                        size="small"
                                        onClick={() => copyTextToClipboard(message.content)}
                                        sx={messageActionButtonSx(isDark, false)}
                                      >
                                        <CopyIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Share prompt">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          openShareDialog({
                                            kind: "prompt",
                                            title: agentDisplayName,
                                            text: message.content,
                                            runNumber: message.runNumber,
                                            agentVersion: agent?.version,
                                            messageId: message.id,
                                          })
                                        }
                                        sx={messageActionButtonSx(isDark, false)}
                                      >
                                        <ShareIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit prompt">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={promptActionsDisabled}
                                          onClick={() => beginPromptEdit(message)}
                                          sx={messageActionButtonSx(isDark, promptActionsDisabled)}
                                        >
                                          <EditIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Rerun prompt">
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={promptActionsDisabled}
                                          onClick={() => rerunPrompt(message.content)}
                                          sx={messageActionButtonSx(isDark, promptActionsDisabled)}
                                        >
                                          <RunIcon />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {/* Welcome Message with Centered Input - Only show when no conversation yet and not viewing history */}
                    {!isPolling &&
                      !finalResult &&
                      !hasRunTriggered &&
                      !selectedRunHistory && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: "760px",
                            boxSizing: "border-box",
                            transform: { xs: "translateY(-18px)", md: "translateY(-32px)" },
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: "100%",
                              maxWidth: "760px",
                              minWidth: 0,
                              overflow: "hidden",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                pl: 1.5,
                                mb: 0.75,
                              }}
                            >
                              {getHelpText()}
                            </Box>
                            <Box
                              ref={inputRef}
                              sx={{
                                width: "100%",
                                minWidth: 0,
                                overflow: "hidden",
                              }}
                            >
                              <TextField
                                multiline
                                minRows={2}
                                maxRows={6}
                                fullWidth
                                placeholder={composerPlaceholder}
                                value={inputValue}
                                onChange={handleInputChange}
                                variant="outlined"
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment
                                      position="end"
                                      sx={composerAdornmentSx}
                                    >
                                      <Tooltip title="Upload files">
                                        <IconButton
                                          size="small"
                                          onClick={() => assetFileInputRef.current?.click()}
                                          disabled={assetUploadBusy}
                                          sx={messageActionButtonSx(isDark, assetUploadBusy)}
                                        >
                                          <AttachFileIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <Tooltip title="Upload folder">
                                        <IconButton
                                          size="small"
                                          onClick={() => assetFolderInputRef.current?.click()}
                                          disabled={assetUploadBusy}
                                          sx={messageActionButtonSx(isDark, assetUploadBusy)}
                                        >
                                          <DriveFolderUploadIcon />
                                        </IconButton>
                                      </Tooltip>
                                      <PinPromptDropdown
                                        onPin={handlePinPrompt}
                                        disabled={!inputValue.trim()}
                                      />
                                      <IconButton
                                        size="small"
                                        onClick={handleEnhanceClick}
                                        disabled={isEnhancing || isPolling}
                                        sx={messageActionButtonSx(isDark, isEnhancing || isPolling)}
                                      >
                                        {isEnhancing ? (
                                          "..."
                                        ) : (
                                          <AutoFixHighIcon />
                                        )}
                                      </IconButton>
                                      {inputValue.trim().length > 0 ? (
                                        <IconButton
                                          onClick={() => handleRun()}
                                          disabled={
                                            isRunning ||
                                            isPolling ||
                                            !inputValue.trim()
                                          }
                                          sx={copperRoundButtonSx}
                                        >
                                          <ArrowUpwardIcon
                                            sx={{
                                              stroke: "white",
                                              strokeWidth: 1.2,
                                            }}
                                          />
                                        </IconButton>
                                      ) : (
                                        /* Show Mic Icon if input is empty */
                                        <Tooltip title={isListening ? "Stop Listening" : "Voice Search"}>
                                          <IconButton
                                            onClick={handleMicClick}
                                            color={
                                              isListening ? "error" : "default"
                                            }
                                            sx={{
                                              ...messageActionButtonSx(isDark, false),
                                              animation: isListening
                                                ? "pulse 1.5s infinite"
                                                : "none",
                                              "@keyframes pulse": {
                                                "0%": { opacity: 1 },
                                                "50%": { opacity: 0.4 },
                                                "100%": { opacity: 1 },
                                              },
                                            }}
                                          >
                                            {isListening ? <StopIcon /> : <MicIcon />}
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </InputAdornment>
                                  ),
                                }}
                                sx={themedComposerSx}
                              />
                              {renderAssetUploadNotice()}
                            </Box>

                            <Popper
                              open={Boolean(anchorEl)}
                              anchorEl={anchorEl}
                              placement="bottom-start"
                              style={{ zIndex: 1300 }}
                              modifiers={[
                                {
                                  name: "offset",
                                  options: { offset: [0, 10] },
                                },
                              ]}
                            >
                              <ClickAwayListener
                                onClickAway={() => setAnchorEl(null)}
                              >
                                <Paper
                                  sx={{
                                    mt: 1,
                                    border: `1px solid ${isDark ? "rgba(224,177,132,0.24)" : "#E0E0E0"}`,
                                    bgcolor: elevatedSurface,
                                    color: "text.primary",
                                    minWidth: 360,
                                    maxHeight: 450,
                                    overflowY: "auto",
                                  }}
                                >
                                  <List dense>
                                    {isLoadingArtifacts ? (
                                      <ListItem>
                                        <ListItemText
                                          primary="Loading artifacts..."
                                          sx={{
                                            color: textMuted,
                                            textAlign: "center",
                                          }}
                                        />
                                      </ListItem>
                                    ) : filteredOptions.length > 0 ? (
                                      filteredOptions.map((file) => (
                                        <ListItem
                                          disablePadding
                                          key={file.id}
                                          divider
                                          sx={{ borderColor: isDark ? "rgba(224,177,132,0.16)" : "#E0E0E0" }}
                                        >
                                          <ListItemButton
                                            onClick={() =>
                                              handleFileSelect(file)
                                            }
                                          >
                                            <Avatar
                                              variant="rounded"
                                              src={file.thumbnail}
                                              sx={{
                                            bgcolor: isDark ? "#0F0D0B" : "black",
                                                width: 32,
                                                height: 32,
                                                mr: 1.5,
                                                borderRadius: 1,
                                                border: file.thumbnail
                                                  ? `1px solid ${isDark ? "rgba(224,177,132,0.22)" : "#e0e0e0"}`
                                                  : "none",
                                              }}
                                              imgProps={{
                                                sx: {
                                                  objectFit: "cover",
                                                },
                                              }}
                                            >
                                              <InsertDriveFileIcon
                                                sx={{
                                                  fontSize: 18,
                                                  color: "#fff",
                                                }}
                                              />
                                            </Avatar>
                                            <ListItemText
                                              primary={file.name}
                                              primaryTypographyProps={{
                                                fontWeight: "medium",
                                              }}
                                              secondary={`Version v${file.version} • Run ${file.runNumber}`}
                                            />
                                          </ListItemButton>
                                        </ListItem>
                                      ))
                                    ) : (
                                      <ListItem>
                                        <ListItemText
                                          primary="No matching files"
                                          sx={{
                                            color: textMuted,
                                            textAlign: "center",
                                          }}
                                        />
                                      </ListItem>
                                    )}

                                    {hasLoadedArtifacts &&
                                      !isLoadingArtifacts && (
                                        <ListItem
                                          sx={{
                                            pt: 1,
                                            pb: 1,
                                            bgcolor: isDark ? "rgba(255,247,239,0.06)" : "#f5f5f5",
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Box
                                                sx={{
                                                  display: "flex",
                                                  justifyContent:
                                                    "space-between",
                                                  alignItems: "center",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{ color: textMuted }}
                                                >
                                                  Showing {fileLibrary.length}{" "}
                                                  files from {loadedRunsCount}{" "}
                                                  most recent runs (out of{" "}
                                                  {totalRunsCount} total)
                                                </Typography>
                                                {hasMoreRuns && (
                                                  <Button
                                                    size="small"
                                                    onClick={loadMoreArtifacts}
                                                    sx={{ ml: 1 }}
                                                  >
                                                    Load More
                                                  </Button>
                                                )}
                                              </Box>
                                            }
                                          />
                                        </ListItem>
                                      )}
                                  </List>
                                </Paper>
                              </ClickAwayListener>
                            </Popper>

                            {/* Visual Badges for JSON Tracking - Centered Version */}
                            {(() => {
                              const includedFiles = selectedFiles.filter((f) =>
                                inputValue.includes(f.name),
                              );
                              const maxVisible = 3;
                              const visibleFiles = includedFiles.slice(
                                0,
                                maxVisible,
                              );
                              const remainingCount =
                                includedFiles.length - maxVisible;

                              return includedFiles.length > 0 ? (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    pt: 0.5,
                                    pb: 1.5,
                                    px: 1.5,
                                    border: `1px solid ${isDark ? "rgba(224,177,132,0.20)" : "#e0e0e0"}`,
                                    borderRadius: 1,
                                    bgcolor: isDark ? "rgba(255,247,239,0.05)" : "rgba(0, 0, 0, 0.02)",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: textMuted,
                                      fontWeight: 500,
                                      mb: 0.5,
                                      display: "block",
                                    }}
                                  >
                                    Files included in the prompt
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    flexWrap="wrap"
                                  >
                                    {visibleFiles.map((file) => (
                                      <Chip
                                        key={file.id}
                                        variant="outlined"
                                        avatar={
                                          file.thumbnail ? (
                                            <Avatar
                                              src={file.thumbnail}
                                              sx={{
                                                bgcolor: elevatedSurface,
                                                width: 24,
                                                height: 24,
                                              }}
                                            />
                                          ) : (
                                            <Avatar
                                              sx={{
                                                bgcolor: "primary.main",
                                                width: 24,
                                                height: 24,
                                              }}
                                            >
                                              <InsertDriveFileIcon
                                                sx={{
                                                  fontSize: 14,
                                                  color: "white",
                                                }}
                                              />
                                            </Avatar>
                                          )
                                        }
                                        label={file.name}
                                        size="small"
                                        sx={{
                                          ...copperChipSx,
                                        }}
                                      />
                                    ))}
                                    {remainingCount > 0 && (
                                      <Chip
                                        label={`+${remainingCount} more`}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          ...copperChipSx,
                                          "& .MuiChip-label": {
                                            fontSize: "0.75rem",
                                            fontWeight: 500,
                                          },
                                        }}
                                      />
                                    )}
                                  </Stack>
                                </Box>
                              ) : null;
                            })()}
                          </Box>
                        </Box>
                      )}

                    {/* Sparkles Thinking Indicator */}
                    {(isPolling || isRunning) && (
                      <ThinkingIndicator
                        thinkingDots={thinkingDots}
                        isReturningToProgress={isReturningToProgress}
                      />
                    )}

                    {chatTranscript.length === 0 && !isPolling && !isRunning && finalResult && (
                      <AgentResponseDisplay
                        finalResult={sanitizeArtifactReferences(finalResult)}
                        feedbackState={feedbackState}
                        onCopy={handleCopyResult}
                        onThumbsUp={handleThumbsUp}
                        onThumbsDown={handleThumbsDown}
                        onRetry={() => handleRun()}
                        markdownComponents={markdownComponents}
                      />
                    )}
                    {runStatus && runStatus.output && (
                      <Accordion
                        sx={{ mt: 2, borderRadius: 1, boxShadow: "none" }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600 }}
                          >
                            Output (raw agent output)
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <Box
                            sx={{
                              wordBreak: "break-word",
                              fontSize: "0.85rem",
                              color: "#334155",
                              lineHeight: 1.7,
                              "& p": { m: 0, mb: 0.75 },
                              "& p:last-child": { mb: 0 },
                              "& ul, & ol": { pl: 2.5, m: 0, mb: 0.75 },
                              "& li": { mb: 0.25 },
                              "& code": { bgcolor: "rgba(0,0,0,0.06)", px: 0.5, py: 0.1, borderRadius: "3px", fontFamily: "monospace", fontSize: "0.78rem" },
                              "& pre": { bgcolor: "rgba(0,0,0,0.04)", p: 1, borderRadius: "6px", overflowX: "auto", m: 0, mb: 0.75 },
                              "& pre code": { bgcolor: "transparent", p: 0 },
                              "& h1, & h2, & h3, & h4": { m: 0, mb: 0.5, fontWeight: 700 },
                              "& strong": { fontWeight: 700 },
                              "& blockquote": { borderLeft: "3px solid #CBD5E1", pl: 1.5, ml: 0, color: "#64748b" },
                              "& a": { color: "#4F6AF0" },
                              "& img": { maxWidth: "100%" },
                            }}
                          >
                            <ReactMarkdown components={markdownComponents}>
                              {sanitizeArtifactReferences(typeof runStatus.output === "string" ? runStatus.output : JSON.stringify(runStatus.output, null, 2))}
                            </ReactMarkdown>
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {runStatus &&
                      runStatus.status === "error" &&
                      (runStatus.message || runStatus.output) && (
                        <Accordion
                          sx={{
                            mt: 2,
                            background: isDark ? "rgba(239,99,99,0.14)" : "#ffebee",
                            border: `1px solid ${isDark ? "rgba(239,99,99,0.28)" : "transparent"}`,
                            borderRadius: 1,
                            boxShadow: "none",
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: isDark ? "#FFDADA" : "#b71c1c" }}
                            >
                              Error (click to expand)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box
                              component="pre"
                              sx={{
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                m: 0,
                                color: "#b71c1c",
                              }}
                            >
                              {runStatus.message}
                              {runStatus.output &&
                                (typeof runStatus.output === "string"
                                  ? `\n${runStatus.output}`
                                  : "")}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                  </Box>
                  {/* End of Gemini-style centered wrapper */}
	                </>
	              )}
	              <Box ref={chatBottomRef} sx={{ height: 1 }} />
	            </Box>
	            {/* End of scrollable content area */}

	            {/* Chat Input Area - Fixed at Bottom - show for active or resumable selected threads */}
	            {((!selectedRunHistory && hasRunTriggered) || Boolean(selectedRunHistory && historicalChatSessionId)) && (
              <Box
                sx={{
                  pt: 1.25,
                  pb: 2,
                  px: { xs: 2, md: 3 },
                  bgcolor: contentSurface,
                }}
              >
                {showNewOutputIndicator && (
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 0.85 }}>
                    <Tooltip title="Jump to latest output">
                      <IconButton
                        size="small"
                        onClick={() => scrollChatToBottom()}
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: elevatedSurface,
                          color: copperText,
                          border: `1px solid ${copperBorder}`,
                          boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.34)" : "0 8px 22px rgba(17, 24, 39, 0.1)",
                          "&:hover": {
                            bgcolor: elevatedSurface,
                            borderColor: isDark ? "rgba(224,177,132,0.46)" : COPPER,
                            transform: "translateY(-1px)",
                          },
                        }}
                      >
                        <KeyboardArrowDownIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mb: 0.5,
                  }}
                >
                  <Box sx={{ width: "100%", maxWidth: "900px", pl: 1.5 }}>
                    {getHelpText()}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    width: "100%",
                    maxWidth: "900px",
                    mx: "auto",
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    ref={inputRef}
                    sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}
                  >
                    <TextField
                      multiline
                      minRows={2}
                      maxRows={6}
                      fullWidth
                      placeholder={composerPlaceholder}
                      value={inputValue}
                      onChange={handleInputChange}
                      variant="outlined"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment
                            position="end"
                            sx={composerAdornmentSx}
                          >
                            <Tooltip title="Upload files">
                              <IconButton
                                size="small"
                                onClick={() => assetFileInputRef.current?.click()}
                                sx={messageActionButtonSx(isDark, assetUploadBusy)}
                                disabled={assetUploadBusy}
                              >
                                <AttachFileIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Upload folder">
                              <IconButton
                                size="small"
                                onClick={() => assetFolderInputRef.current?.click()}
                                sx={messageActionButtonSx(isDark, assetUploadBusy)}
                                disabled={assetUploadBusy}
                              >
                                <DriveFolderUploadIcon />
                              </IconButton>
                            </Tooltip>
                            <PinPromptDropdown
                              onPin={handlePinPrompt}
                              disabled={!inputValue.trim()}
                            />
                            <IconButton
                              size="small"
                              onClick={handleEnhanceClick}
                              sx={messageActionButtonSx(isDark, isEnhancing || isPolling)}
                              disabled={isEnhancing || isPolling}
                            >
                              {<AutoFixHighIcon />}
                            </IconButton>

                            {inputValue.trim().length > 0 ? (
                              /* Show Arrow Up Icon if input has text */
                              <IconButton
                                onClick={() => handleRun()}
                                disabled={isRunning || isPolling || !inputValue.trim()}
                                sx={copperRoundButtonSx}
                              >
                                <ArrowUpwardIcon
                                  sx={{ stroke: "white", strokeWidth: 1.2 }}
                                />
                              </IconButton>
                            ) : (
                              /* Show Mic Icon if input is empty */
                              <Tooltip title={isListening ? "Stop Listening" : "Voice Search"}>
                                <IconButton
                                  onClick={handleMicClick}
                                  color={isListening ? "error" : "default"}
                                  sx={{
                                    ...messageActionButtonSx(isDark, false),
                                    animation: isListening ? "pulse 1.5s infinite" : "none",
                                    "@keyframes pulse": {
                                      "0%": { opacity: 1 },
                                      "50%": { opacity: 0.4 },
                                      "100%": { opacity: 1 },
                                    },
                                  }}
                                >
                                  {isListening ? <StopIcon /> : <MicIcon />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </InputAdornment>
                        ),
                      }}
                      sx={themedComposerSx}
                    />
                    {renderAssetUploadNotice()}
                  </Box>

                  <Popper
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    style={{ zIndex: 1300 }}
                    modifiers={[
                      { name: "offset", options: { offset: [0, 10] } },
                    ]}
                  >
                    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
                      <Paper
                        sx={{
                          mt: 1,
                          border: `1px solid ${isDark ? "rgba(224,177,132,0.24)" : "#E0E0E0"}`,
                          bgcolor: elevatedSurface,
                          color: "text.primary",
                          minWidth: 360,
                          maxHeight: 450,
                          overflowY: "auto",
                        }}
                      >
                        <List dense>
                          {isLoadingArtifacts ? (
                            <ListItem>
                              <ListItemText
                                primary="Loading artifacts..."
                                sx={{ color: textMuted, textAlign: "center" }}
                              />
                            </ListItem>
                          ) : filteredOptions.length > 0 ? (
                            filteredOptions.map((file) => (
                              <ListItem
                                disablePadding
                                key={file.id}
                                divider
                                sx={{ borderColor: isDark ? "rgba(224,177,132,0.16)" : "#E0E0E0" }}
                              >
                                <ListItemButton
                                  onClick={() => handleFileSelect(file)}
                                >
                                  <ListItemAvatar sx={{ minWidth: 50 }}>
                                    <Avatar
                                      variant="rounded"
                                      src={file.thumbnail}
                                      sx={{
                                        bgcolor: file.thumbnail
                                          ? "transparent"
                                          : "primary.main",
                                        width: 40,
                                        height: 40,
                                        border: file.thumbnail
                                          ? `1px solid ${isDark ? "rgba(224,177,132,0.22)" : "#e0e0e0"}`
                                          : "none",
                                      }}
                                      imgProps={{
                                        sx: {
                                          objectFit: "cover",
                                        },
                                      }}
                                    >
                                      {!file.thumbnail && (
                                        <Description fontSize="small" />
                                      )}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={file.name}
                                    primaryTypographyProps={{
                                      fontWeight: "medium",
                                    }}
                                    secondary={
                                      <Typography
                                        variant="caption"
                                        sx={{ color: textMuted }}
                                      >
                                        Version:{" "}
                                        <span style={{ color: isDark ? "#F2C69B" : "#8d6e63" }}>
                                          v{file.version}
                                        </span>{" "}
                                        • Run: #{file.runNumber}
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              </ListItem>
                            ))
                          ) : (
                            <ListItem>
                              <ListItemText
                                primary="No previous artifacts found"
                                sx={{ color: textMuted, textAlign: "center" }}
                              />
                            </ListItem>
                          )}

                          {/* Load More Button and Status */}
                          {hasLoadedArtifacts && !isLoadingArtifacts && (
                            <ListItem
                              sx={{
                                flexDirection: "column",
                                gap: 1,
                                py: 1.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ color: textMuted, textAlign: "center" }}
                              >
                                Showing artifacts from {loadedRunsCount} of{" "}
                                {totalRunsCount} runs
                                {filteredOptions.length > 0 &&
                                  ` (${filteredOptions.length} files)`}
                              </Typography>
                              {hasMoreRuns && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    loadMoreArtifacts();
                                  }}
                                  sx={{ textTransform: "none" }}
                                >
                                  Load More Runs
                                </Button>
                              )}
                            </ListItem>
                          )}
                        </List>
                      </Paper>
                    </ClickAwayListener>
                  </Popper>

                  {/* Visual Badges for JSON Tracking */}
                  {(() => {
                    const includedFiles = selectedFiles.filter((f) =>
                      inputValue.includes(f.name),
                    );
                    const maxVisible = 3;
                    const visibleFiles = includedFiles.slice(0, maxVisible);
                    const remainingCount = includedFiles.length - maxVisible;

                    return includedFiles.length > 0 ? (
                      <Box
                        sx={{
                          mt: 1.5,
                          pt: 0.5,
                          pb: 1.5,
                          px: 1.5,
                          border: `1px solid ${isDark ? "rgba(224,177,132,0.20)" : "#e0e0e0"}`,
                          borderRadius: 1,
                          bgcolor: isDark ? "rgba(255,247,239,0.05)" : "rgba(0, 0, 0, 0.02)",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: textMuted,
                            fontWeight: 500,
                            mb: 0.5,
                            display: "block",
                          }}
                        >
                          Files included in the prompt
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {visibleFiles.map((file) => (
                            <Chip
                              key={file.id}
                              label={file.name}
                              size="small"
                              variant="outlined"
                              avatar={
                                file.thumbnail ? (
                                  <Avatar
                                    src={file.thumbnail}
                                    sx={{
                                      width: 24,
                                      height: 24,
                                      border: `1px solid ${isDark ? "rgba(224,177,132,0.22)" : "#e0e0e0"}`,
                                    }}
                                  />
                                ) : undefined
                              }
                              icon={
                                !file.thumbnail ? (
                                  <Description
                                    sx={{
                                      fontSize: "14px",
                                      color: "primary.main",
                                    }}
                                  />
                                ) : undefined
                              }
                              sx={{
                                ...copperChipSx,
                              }}
                            />
                          ))}
                          {remainingCount > 0 && (
                            <Tooltip
                              title={
                                <Box>
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Additional files:
                                  </Typography>
                                  {includedFiles
                                    .slice(maxVisible)
                                    .map((file) => (
                                      <Box
                                        key={file.id}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          mt: 0.5,
                                        }}
                                      >
                                        {file.thumbnail ? (
                                          <Avatar
                                            src={file.thumbnail}
                                            sx={{
                                              width: 16,
                                              height: 16,
                                              mr: 0.5,
                                              border: `1px solid ${isDark ? "rgba(224,177,132,0.22)" : "#e0e0e0"}`,
                                            }}
                                          />
                                        ) : (
                                          <Description
                                            sx={{ fontSize: 16, mr: 0.5 }}
                                          />
                                        )}
                                        <Typography variant="caption">
                                          {file.name}
                                        </Typography>
                                      </Box>
                                    ))}
                                </Box>
                              }
                              arrow
                            >
                              <Chip
                                label={`+${remainingCount} more`}
                                size="small"
                                variant="outlined"
                                icon={
                                  <AttachFileIcon sx={{ fontSize: "14px" }} />
                                }
                                sx={{
                                  ...copperChipSx,
                                  borderStyle: "dashed",
                                  cursor: "pointer",
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </Box>
                    ) : null;
                  })()}
                </Box>
              </Box>
            )}
            {/* End of sticky input */}
          </Box>
          <PromptEnhancerDialog
            agent={agentCard}
            originalPrompt={inputValue}
            enhancedPrompt={enhancedSuggestion}
            isEnhancing={isEnhancing}
            isOpen={isModalOpen}
            onAccept={handleAcceptEnhancement}
            onReject={handleRejectEnhancement}
            onRegenerate={regeneratePrompt}
          />
        </Box>
      </Box>
      <Dialog
        open={Boolean(shareDialog)}
        onClose={() => setShareDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "28px",
            p: 0.5,
            bgcolor: contentSurface,
            color: "text.primary",
            boxShadow: isDark ? "0 28px 80px rgba(0,0,0,0.55)" : "0 28px 80px rgba(15, 23, 42, 0.24)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            pb: 1,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: textPrimary }}>
              Share {shareDialog?.kind === "prompt" ? "Prompt" : "Result"}
            </Typography>
            <Typography variant="body2" sx={{ color: textMuted, mt: 0.35 }}>
              People with workspace access can open this linked run.
            </Typography>
          </Box>
          <IconButton onClick={() => setShareDialog(null)} sx={messageActionButtonSx(isDark, false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Box
            sx={{
              border: `1px solid ${isDark ? "rgba(224,177,132,0.22)" : "rgba(226, 232, 240, 0.9)"}`,
              borderRadius: "18px",
              p: 2,
              bgcolor: elevatedSurface,
              boxShadow: isDark ? "inset 0 1px 0 rgba(255,247,239,0.08)" : "inset 0 1px 0 rgba(255,255,255,0.75)",
            }}
          >
            <Typography variant="subtitle2" sx={{ color: textPrimary, fontWeight: 800, mb: 1 }}>
              {shareDialog?.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: textMuted,
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                maxHeight: 150,
                overflow: "hidden",
              }}
            >
              {shareDialog?.text || "No text preview available."}
            </Typography>
          </Box>
          <Divider sx={{ my: 2.25 }} />
          <Stack direction="row" spacing={1.25} justifyContent="center" flexWrap="wrap" useFlexGap>
            <Tooltip title={shareCopied ? "Link copied" : "Copy link"}>
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={copyShareLink}
                sx={{
                  bgcolor: COPPER,
                  color: "#fff",
                  borderRadius: "999px",
                  px: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  "&:hover": { bgcolor: isDark ? "#E0B184" : COPPER_DARK },
                }}
              >
                {shareCopied ? "Copied" : "Copy Link"}
              </Button>
            </Tooltip>
            <Tooltip title="Use system share">
              <IconButton onClick={shareNativeOrCopy} sx={messageActionButtonSx(isDark, false)}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on X">
              <IconButton onClick={() => openShareTarget("x")} sx={messageActionButtonSx(isDark, false)}>
                <Typography sx={{ fontSize: 15, fontWeight: 900, lineHeight: 1 }}>X</Typography>
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on LinkedIn">
              <IconButton onClick={() => openShareTarget("linkedin")} sx={messageActionButtonSx(isDark, false)}>
                <LinkedInIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Share on Reddit">
              <IconButton onClick={() => openShareTarget("reddit")} sx={messageActionButtonSx(isDark, false)}>
                <RedditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Open link in new tab">
              <IconButton
                onClick={() => shareDialog && window.open(shareDialog.url, "_blank", "noopener,noreferrer")}
                sx={messageActionButtonSx(isDark, false)}
              >
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            onClick={() => setShareDialog(null)}
            sx={{
              color: textMuted,
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentRunPage;
